import { AvailableGifts, AppliedPromotionsType, AttributesTypes, Checkout, CheckoutLines, GiftPromotion, GiftPromotionRuleGroup } from "@/common/lib/types";
import { giftTypes, promotionType, rewardValueTypes, ProductType, SaleorAttributes, sessionStorageKeys, MetaDataKeys } from "@/apiUtils/constants";
import { hasArrayElements, toPrecise } from "@/apiUtils/commonUtils";
import {
  getCataloguePromotionUnitPrice,
  getCheckoutPriceAmount,
  getEligibleCataloguePromotionForCard,
  getLineRetailUnitPrice,
  isCheckoutPromotionLimitReached,
  isPaidCheckoutMerchandiseLine,
  preferVatInclusiveAmount,
  resolveCatalogVoucherPaidTotal,
  resolveTaxedGrossAmount,
  sumMerchandiseRetailSubtotal,
} from "@/utils/promotionUtils";
import { isCheckoutGiftLine } from "@/utils/checkoutGiftLine";

export { isCheckoutGiftLine };

export function normalizeRewardValueType(rewardValueType?: string | null): string {
  return `${rewardValueType ?? ""}`.toUpperCase();
}

export function isFixedRewardType(rewardValueType?: string | null): boolean {
  return normalizeRewardValueType(rewardValueType) === rewardValueTypes.FIXED;
}

export function isPercentageRewardType(rewardValueType?: string | null): boolean {
  return normalizeRewardValueType(rewardValueType) === rewardValueTypes.PERCENTAGE;
}

function getSameCatalogueListPrice(line?: CheckoutLines | null): number {
  return (
    line?.variant?.pricing?.priceUndiscounted?.gross?.amount ??
    line?.variant?.pricing?.costPrice?.gross?.amount ??
    line?.unitPrice?.gross?.amount ??
    line?.variant?.pricing?.price?.gross?.amount ??
    0
  );
}

function getSameCataloguePaidUnitPrice(line?: CheckoutLines | null): number | null {
  const paid = line?.unitPrice?.gross?.amount ?? line?.variant?.pricing?.price?.gross?.amount;
  return paid != null ? paid : null;
}

export function isSameCatalogueFullDiscountLine(line?: CheckoutLines | null): boolean {
  const promo = line?.appliedPromotions?.[0];
  if (!promo || promo.giftType !== giftTypes.SAME_CATALOGUE) {
    return false;
  }

  const rewardValue = Number(promo.rewardValue ?? 0);
  const rewardType = normalizeRewardValueType(promo.rewardValueType);

  if (rewardType === rewardValueTypes.PERCENTAGE) {
    return rewardValue >= 100;
  }

  if (rewardType === rewardValueTypes.FIXED) {
    const listPrice = getSameCatalogueListPrice(line);
    const paidUnitPrice = getSameCataloguePaidUnitPrice(line);

    if (paidUnitPrice != null && paidUnitPrice <= 0) {
      return true;
    }

    return listPrice > 0 && rewardValue >= listPrice;
  }

  return false;
}

export function isPartialDiscountSameCatalogueLine(line?: CheckoutLines | null): boolean {
  const promo = line?.appliedPromotions?.[0];
  if (!promo || promo.giftType !== giftTypes.SAME_CATALOGUE) {
    return false;
  }

  if (isSameCatalogueFullDiscountLine(line)) {
    return false;
  }

  const rewardValue = Number(promo.rewardValue ?? 0);
  const rewardType = normalizeRewardValueType(promo.rewardValueType);

  if (rewardType === rewardValueTypes.PERCENTAGE) {
    return rewardValue > 0 && rewardValue < 100;
  }

  if (rewardType === rewardValueTypes.FIXED) {
    const listPrice = getSameCatalogueListPrice(line);
    const paidUnitPrice = getSameCataloguePaidUnitPrice(line);

    if (paidUnitPrice != null && listPrice > 0) {
      return paidUnitPrice > 0 && paidUnitPrice < listPrice;
    }

    return rewardValue > 0 && listPrice > 0 && rewardValue < listPrice;
  }

  return false;
}

export function getSameCatalogueDiscountDisplay(
  promotion: AppliedPromotionsType | undefined,
  listPrice: number,
  discountedPrice: number,
): { isFlatOff: boolean; discount: number } {
  if (!promotion || promotion.giftType !== giftTypes.SAME_CATALOGUE) {
    return { isFlatOff: false, discount: 0 };
  }

  const rewardValue = Number(promotion.rewardValue ?? 0);
  const isFlatOff = isFixedRewardType(promotion.rewardValueType);

  if (rewardValue > 0) {
    if (isFlatOff) {
      return { isFlatOff: true, discount: toPrecise(rewardValue) };
    }

    if (rewardValue < 100) {
      return { isFlatOff: false, discount: rewardValue };
    }
  }

  const calculatedDiscount = isFlatOff
    ? toPrecise(listPrice - discountedPrice)
    : Math.abs(((discountedPrice - listPrice) / Math.abs(listPrice || 1)) * 100);

  return { isFlatOff, discount: toPrecise(calculatedDiscount) };
}

export function allowsSameCatalogueLineQuantityChange(line?: CheckoutLines | null): boolean {
  return isSameCatalogueFullDiscountLine(line);
}

export function shouldShowLineAsFreeGift(line?: CheckoutLines | null): boolean {
  if (!line) {
    return false;
  }

  const promo = line.appliedPromotions?.[0];

  if (promo?.giftType === giftTypes.SAME_CATALOGUE) {
    return isSameCatalogueFullDiscountLine(line);
  }

  if (isPartialDiscountSameCatalogueLine(line)) {
    return false;
  }

  if (line.isGift) {
    return true;
  }

  if (promo?.giftType === giftTypes.FREE_GIFT) {
    return !isPaidCheckoutMerchandiseLine(line);
  }

  if (line.isBuyxGetyGift) {
    const triggerPromo = line.appliedPromotions?.find(
      (p) => p.promotion?.type === promotionType.BUYXGETY && p.giftType !== giftTypes.FREE_GIFT,
    );
    if (triggerPromo) {
      return false;
    }
    // Coupon refetch often clears FREE_GIFT promo metadata — zero-charge BXGY gifts stay Free.
    if (!isPaidCheckoutMerchandiseLine(line)) {
      return true;
    }
    return Boolean(
      line.isGift || line.appliedPromotions?.some((p) => p.giftType === giftTypes.FREE_GIFT),
    );
  }

  const unitAmount = line.unitPrice?.gross?.amount ?? line.totalPrice?.gross?.amount;
  if (
    (unitAmount == null || unitAmount <= 0.01) &&
    (isCheckoutGiftLine(line) || line.isBuyxGetyGift)
  ) {
    return true;
  }

  return false;
}

export type GiftCheckoutContext = {
  lines?: CheckoutLines[] | null;
  availableGiftPromotions?: GiftPromotion[] | null;
  availableGifts?: AvailableGifts[] | null;
  selectedGiftVariant?: string | null;
  currency?: string;
  enrichedGiftCatalog?: Record<string, AvailableGifts> | null;
  allowSyntheticGifts?: boolean;
};

export type PersistedGiftCheckoutSelection = {
  selectedGiftVariantId?: string | null;
  selectedGiftVariantIds?: string[] | null;
  selectionsByRuleId?: Record<string, string[]> | null;
  availableGiftPromotions?: GiftPromotion[] | null;
  availableGifts?: AvailableGifts[] | null;
};

export function isSyntheticGiftLine(line?: CheckoutLines | null): boolean {
  return Boolean(line?.id?.startsWith("available-gift-"));
}

function hasGiftAttribute(attributes: AttributesTypes[] | undefined, attributeName: string): boolean {
  const attribute = attributes?.find((attr) => attr?.attribute?.slug === attributeName);
  return Boolean(attribute?.values?.length);
}

function getGiftAttributeValue(
  attributes: AttributesTypes[] | undefined,
  attributeName: string,
): string | null {
  const attribute = attributes?.find((attr) => attr?.attribute?.slug === attributeName);
  const value = attribute?.values?.[0];

  if (!value) {
    return null;
  }

  const resolved = value.name || value.plainText || value.translation?.name || value.referenceEntity;
  return resolved ? String(resolved) : null;
}

export function inferProductTypeSlugFromGiftAttributes(
  variantAttributes?: AttributesTypes[],
  productAttributes?: AttributesTypes[],
): string {
  const allAttributes: AttributesTypes[] = [...(variantAttributes ?? []), ...(productAttributes ?? [])];

  if (hasGiftAttribute(allAttributes, SaleorAttributes.CL_POWER)) {
    return hasGiftAttribute(allAttributes, SaleorAttributes.CL_COLOR) ? ProductType.CL_COLOR : ProductType.CL_CLEAR;
  }

  if (hasGiftAttribute(allAttributes, SaleorAttributes.ACC_TYPE)) {
    if (hasGiftAttribute(allAttributes, SaleorAttributes.ACC_POWER)) return ProductType.ACC_BY_COLOR_BY_POWER;
    if (hasGiftAttribute(allAttributes, SaleorAttributes.ACC_VOLUME)) return ProductType.ACC_VOLUME;
    return ProductType.ACC_BY_COLOR;
  }

  if (hasGiftAttribute(allAttributes, SaleorAttributes.LENS_COLOR)) return ProductType.SUNGLASSES;

  if (hasGiftAttribute(allAttributes, SaleorAttributes.FRAME_COLOR)) return ProductType.EYEGLASSES;

  return "";
}

function resolveGiftVariantDisplayName(gift: AvailableGifts): string {
  const product = gift.product;
  const variantTitle = getGiftAttributeValue(gift.attributes, SaleorAttributes.TITLE);
  const productTitle = getGiftAttributeValue(product?.attributes, SaleorAttributes.TITLE);

  return variantTitle || productTitle || product?.name || gift.name;
}

function giftAttributeHasValue(attribute?: AttributesTypes): boolean {
  const value = attribute?.values?.[0];
  if (!value) {
    return false;
  }

  return Boolean(
    value.name ||
      value.plainText ||
      value.referenceEntity ||
      value.translation?.name ||
      value.translation?.plainText,
  );
}

function countPopulatedGiftAttributes(attributes?: AttributesTypes[]): number {
  return attributes?.filter(giftAttributeHasValue).length ?? 0;
}

export function hasPopulatedGiftCatalogData(gift?: AvailableGifts): boolean {
  if (!gift) {
    return false;
  }

  return Boolean(
    inferProductTypeSlugFromGiftAttributes(gift.attributes, gift.product?.attributes) ||
      getGiftAttributeValue(gift.attributes, SaleorAttributes.TITLE) ||
      getGiftAttributeValue(gift.product?.attributes, SaleorAttributes.TITLE),
  );
}

export function variantDetailsToAvailableGift(variant: {
  id: string;
  name?: string | null;
  sku?: string | null;
  landingPageBaseUrl?: string | null;
  attributes?: AttributesTypes[] | null;
  product?: {
    id?: string;
    name?: string | null;
    slug?: string | null;
    brand?: AvailableGifts["product"]["brand"];
    productType?: { slug?: string; name?: string };
    attributes?: AttributesTypes[] | null;
    translation?: AvailableGifts["product"]["translation"];
    media?: AvailableGifts["product"]["media"];
  } | null;
}): AvailableGifts {
  return {
    id: variant.id,
    name: variant.name ?? "",
    sku: variant.sku ?? undefined,
    landingPageBaseUrl: variant.landingPageBaseUrl ?? undefined,
    attributes: variant.attributes ?? [],
    product: {
      id: variant.product?.id ?? "",
      name: variant.product?.name ?? variant.name ?? "",
      slug: variant.product?.slug ?? undefined,
      brand: variant.product?.brand ?? { name: "" },
      productType: variant.product?.productType,
      attributes: variant.product?.attributes ?? [],
      translation: variant.product?.translation,
      media: variant.product?.media,
    },
  };
}

function mergeAvailableGiftVariant(previous?: AvailableGifts, next?: AvailableGifts): AvailableGifts | undefined {
  if (!next) {
    return previous;
  }

  if (!previous || previous.id !== next.id) {
    return next;
  }

  const nextVariantAttrs = countPopulatedGiftAttributes(next.attributes);
  const prevVariantAttrs = countPopulatedGiftAttributes(previous.attributes);
  const nextProductAttrs = countPopulatedGiftAttributes(next.product?.attributes);
  const prevProductAttrs = countPopulatedGiftAttributes(previous.product?.attributes);

  return {
    ...previous,
    ...next,
    sku: next.sku || previous.sku,
    landingPageBaseUrl: next.landingPageBaseUrl || previous.landingPageBaseUrl,
    attributes: nextVariantAttrs >= prevVariantAttrs ? next.attributes : previous.attributes,
    product: {
      ...previous.product,
      ...next.product,
      slug: next.product?.slug || previous.product?.slug,
      attributes: nextProductAttrs >= prevProductAttrs ? next.product?.attributes : previous.product?.attributes,
      productType: next.product?.productType?.slug ? next.product.productType : previous.product?.productType,
    },
  };
}

export function mergeGiftCatalogEntry(
  previous?: AvailableGifts,
  next?: AvailableGifts,
): AvailableGifts | undefined {
  return mergeAvailableGiftVariant(previous, next);
}

/** PDP link for cart lines — prefer backend landingPageBaseUrl over legacy /product/{slug}/{id}. */
export function resolveCheckoutLineProductUrl(variant?: {
  id?: string | null;
  landingPageBaseUrl?: string | null;
  product?: { slug?: string | null } | null;
} | null): string {
  if (!variant) {
    return "";
  }

  if (variant.landingPageBaseUrl) {
    return variant.landingPageBaseUrl;
  }

  const slug = variant.product?.slug;
  if (slug && variant.id && !/^\d+$/.test(slug)) {
    return `/product/${slug}/${variant.id}`;
  }

  return "";
}

function mergeGiftPromotionDetails(previous?: GiftPromotion, next?: GiftPromotion): GiftPromotion | undefined {
  if (!next) {
    return previous;
  }

  if (!previous || previous.promotionRuleId !== next.promotionRuleId) {
    return next;
  }

  const prevVariants = previous.eligibleGiftVariants ?? [];
  const mergedEligible = (next.eligibleGiftVariants ?? []).map((variant) => {
    const prevVariant = prevVariants.find((item) => item.id === variant.id);
    return mergeAvailableGiftVariant(prevVariant, variant) ?? variant;
  });

  prevVariants.forEach((variant) => {
    if (!mergedEligible.some((item) => item.id === variant.id)) {
      mergedEligible.push(variant);
    }
  });

  const prevSelected = prevVariants.find((item) => item.id === next.selectedGiftVariant?.id);
  // Coupon/refetch responses often clear selectedGiftVariant — keep the prior selection
  // unless the shopper explicitly opted out of this gift promo.
  const resolvedSelected = next.selectedGiftVariant
    ? mergeAvailableGiftVariant(prevSelected, next.selectedGiftVariant) ?? next.selectedGiftVariant
    : next.optedOut
      ? undefined
      : previous.selectedGiftVariant ?? next.selectedGiftVariant;

  return {
    ...next,
    optedOut: next.optedOut ?? previous.optedOut,
    eligibleGiftVariants: mergedEligible,
    selectedGiftVariant: resolvedSelected,
  };
}

function pickRicherAttributes(
  primary?: AttributesTypes[],
  secondary?: AttributesTypes[],
): AttributesTypes[] {
  const primaryCount = countPopulatedGiftAttributes(primary);
  const secondaryCount = countPopulatedGiftAttributes(secondary);
  if (secondaryCount > primaryCount) {
    return secondary ?? [];
  }
  return primary ?? [];
}

function findCatalogGift(context: GiftCheckoutContext, variantId?: string): AvailableGifts | undefined {
  if (!variantId) {
    return undefined;
  }

  const fromEligible = context.availableGiftPromotions
    ?.flatMap((promo) => promo.eligibleGiftVariants ?? [])
    .find((item) => item.id === variantId);

  const fromSelected = context.availableGiftPromotions
    ?.map((promo) => promo.selectedGiftVariant)
    .find((item) => item?.id === variantId);

  const fromAvailableGifts = context.availableGifts?.find((item) => item.id === variantId);
  const fromEnriched = context.enrichedGiftCatalog?.[variantId];

  return (
    mergeAvailableGiftVariant(
      mergeAvailableGiftVariant(
        fromEnriched,
        mergeAvailableGiftVariant(fromEligible, fromSelected ?? undefined),
      ),
      fromAvailableGifts,
    ) ??
    fromEnriched ??
    fromEligible ??
    fromSelected ??
    fromAvailableGifts
  );
}

function mergeGiftLineVariantForDisplay(
  catalogVariant: CheckoutLines["variant"],
  apiVariant?: CheckoutLines["variant"] | null,
): CheckoutLines["variant"] {
  if (!apiVariant) {
    return catalogVariant;
  }

  const mergedVariantAttributes = pickRicherAttributes(catalogVariant?.attributes, apiVariant.attributes);
  const mergedProductAttributes = pickRicherAttributes(
    catalogVariant?.product?.attributes,
    apiVariant.product?.attributes,
  );
  const catalogProductType = catalogVariant?.product?.productType?.slug;
  const apiProductType = apiVariant.product?.productType?.slug;
  const catalogDisplayName = catalogVariant?.name;
  const apiDisplayName = apiVariant.name;
  const catalogPopulated =
    countPopulatedGiftAttributes(catalogVariant?.attributes) +
    countPopulatedGiftAttributes(catalogVariant?.product?.attributes);
  const apiPopulated =
    countPopulatedGiftAttributes(apiVariant.attributes) +
    countPopulatedGiftAttributes(apiVariant.product?.attributes);

  return {
    ...catalogVariant,
    ...apiVariant,
    name:
      catalogPopulated >= apiPopulated
        ? catalogDisplayName || apiDisplayName
        : apiDisplayName || catalogDisplayName,
    attributes: mergedVariantAttributes,
    sku: apiVariant.sku || catalogVariant?.sku,
    quantityAvailable: apiVariant.quantityAvailable ?? catalogVariant?.quantityAvailable,
    pricing: apiVariant.pricing ?? catalogVariant?.pricing,
    landingPageBaseUrl: apiVariant.landingPageBaseUrl ?? catalogVariant?.landingPageBaseUrl,
    availablePromotions: apiVariant.availablePromotions ?? catalogVariant?.availablePromotions,
    product: {
      ...catalogVariant?.product,
      ...apiVariant.product,
      attributes: mergedProductAttributes,
      productType: apiProductType
        ? apiVariant.product?.productType
        : catalogProductType
          ? catalogVariant?.product?.productType
          : apiVariant.product?.productType ?? catalogVariant?.product?.productType,
      brand: apiVariant.product?.brand?.name ? apiVariant.product.brand : catalogVariant?.product?.brand,
      translation: apiVariant.product?.translation ?? catalogVariant?.product?.translation,
    },
  };
}

/** Counts selected gift qty per variant from promos (supports qty > 1 of same gift). */
function getGiftSelectionQuantityByVariantId(context: GiftCheckoutContext): Map<string, number> {
  const counts = new Map<string, number>();
  const bump = (variantId?: string | null) => {
    if (!variantId) {
      return;
    }
    counts.set(variantId, (counts.get(variantId) ?? 0) + 1);
  };

  // Count every pick occurrence so qty stepper ([sku, sku]) shows Qty: 2,
  // while two different SKUs each stay Qty: 1.
  getAvailableOfferPromotions(context.availableGiftPromotions).forEach((promo) => {
    sanitizeOfferSelectionIds(promo, getSelectedGiftVariantIds(promo)).forEach((id) => bump(id));
  });

  return counts;
}

function enrichVisibleGiftLines(visible: CheckoutLines[], context: GiftCheckoutContext): CheckoutLines[] {
  const currency = context.currency ?? "";
  const allEligibleGiftVariantIds = getAllEligibleGiftVariantIds(context);
  const selectionQtyByVariantId = getGiftSelectionQuantityByVariantId(context);

  const enrichLine = (line: CheckoutLines): CheckoutLines => {
    const variantId = line.variant?.id;
    if (
      !variantId ||
      (!isLineGiftForDisplay(line, allEligibleGiftVariantIds) && !allEligibleGiftVariantIds.includes(variantId))
    ) {
      return line;
    }

    const catalogGift = findCatalogGift(context, variantId);
    if (!catalogGift || context.allowSyntheticGifts === false) {
      const selectionQty = selectionQtyByVariantId.get(variantId) ?? 0;
      if (selectionQty > 0) {
        return { ...line, quantity: selectionQty };
      }
      return line;
    }

    const resolved = resolveGiftDisplayLine(line, catalogGift, currency);
    const selectionQty = selectionQtyByVariantId.get(variantId) ?? 0;
    if (selectionQty > 0) {
      return selectionQty === resolved.quantity ? resolved : { ...resolved, quantity: selectionQty };
    }
    return resolved;
  };

  return visible.map((line) => {
    const enriched = enrichLine(line);
    if (!line.addOns?.length) {
      return enriched;
    }

    return {
      ...enriched,
      addOns: line.addOns.map((addOn) => {
        if (!isCheckoutGiftLine(addOn) && !addOn.buyGetGiftFlag) {
          return addOn;
        }
        return enrichLine(addOn);
      }),
    };
  });
}

function resolveGiftDisplayLine(
  apiLine: CheckoutLines | null,
  gift: AvailableGifts,
  currency: string,
): CheckoutLines {
  const catalogLine = checkoutLineFromAvailableGift(gift, currency);

  if (!apiLine || isSyntheticGiftLine(apiLine)) {
    return catalogLine;
  }

  return {
    ...catalogLine,
    ...apiLine,
    id: apiLine.id,
    quantity: apiLine.quantity,
    totalPrice: apiLine.totalPrice,
    unitPrice: apiLine.unitPrice,
    undiscountedUnitPrice: apiLine.undiscountedUnitPrice,
    // Gift display lines must keep gift flags even when coupon refetch clears them.
    isGift: true,
    isBuyxGetyGift: true,
    appliedPromotions: apiLine.appliedPromotions?.length ? apiLine.appliedPromotions : catalogLine.appliedPromotions,
    variant: mergeGiftLineVariantForDisplay(catalogLine.variant, apiLine.variant),
  };
}

export function countEligibleGiftVariants(promotions?: GiftPromotion[] | null): number {
  return promotions?.reduce((acc, promo) => acc + (promo.eligibleGiftVariants?.length || 0), 0) ?? 0;
}

/** One breakdown entry per rule the promo already carries, so re-merging stays idempotent. */
function describePromoRules(promo: GiftPromotion): GiftPromotionRuleGroup[] {
  if (promo.ruleBreakdown?.length) {
    return promo.ruleBreakdown.map((entry) => ({ ...entry, variantIds: [...entry.variantIds] }));
  }

  return [
    {
      promotionRuleId: promo.promotionRuleId,
      promotionName: promo.promotionName,
      giftQuantity: Math.max(1, promo.giftQuantity ?? 1),
      variantIds: [],
    },
  ];
}

/** Split the merged eligible gifts back into the rule that owns each of them. */
function attachRuleVariantIds(promo: GiftPromotion): GiftPromotionRuleGroup[] {
  const entries = (promo.ruleBreakdown ?? []).map((entry) => ({ ...entry, variantIds: [] as string[] }));
  if (!entries.length) {
    return entries;
  }

  const byRuleId = new Map(entries.map((entry) => [entry.promotionRuleId, entry]));
  (promo.eligibleGiftVariants ?? []).forEach((variant) => {
    const ruleId = promo.variantRuleMap?.[variant.id];
    const entry = (ruleId ? byRuleId.get(ruleId) : undefined) ?? entries[0];
    if (!entry.variantIds.includes(variant.id)) {
      entry.variantIds.push(variant.id);
    }
  });

  return entries;
}

/** Each rule enforces its own gift quantity, so a full rule cannot eat another rule's slot. */
export function capGiftSelectionPerRule<T>(
  items: T[],
  rules: GiftPromotionRuleGroup[],
  getVariantId: (item: T) => string | undefined,
): T[] {
  const ownerByVariantId = new Map<string, string>();
  const limitByRuleId = new Map<string, number>();
  rules.forEach((rule) => {
    limitByRuleId.set(rule.promotionRuleId, Math.max(1, rule.giftQuantity));
    rule.variantIds.forEach((variantId) => {
      if (!ownerByVariantId.has(variantId)) {
        ownerByVariantId.set(variantId, rule.promotionRuleId);
      }
    });
  });

  const total = rules.reduce((acc, rule) => acc + Math.max(1, rule.giftQuantity), 0);
  const usedByRuleId = new Map<string, number>();
  const kept: T[] = [];

  items.forEach((item) => {
    if (kept.length >= total) {
      return;
    }

    const ruleId = ownerByVariantId.get(getVariantId(item) ?? "");
    if (!ruleId) {
      kept.push(item);
      return;
    }

    const used = usedByRuleId.get(ruleId) ?? 0;
    if (used >= (limitByRuleId.get(ruleId) ?? 1)) {
      return;
    }
    usedByRuleId.set(ruleId, used + 1);
    kept.push(item);
  });

  return kept;
}

/** Rules folded into one offer card — only present when the promotion has 2+ gift rules. */
export function getGiftRuleGroups(promo?: GiftPromotion | null): GiftPromotionRuleGroup[] {
  return (promo?.ruleBreakdown?.length ?? 0) > 1 ? promo?.ruleBreakdown ?? [] : [];
}

/** Limit that applies to one gift tile: its own rule when the card merges several rules. */
export function getGiftSelectionScope(
  promo: GiftPromotion,
  variantId: string,
): { limit: number; variantIds: string[] | null } {
  const rule = getGiftRuleGroups(promo).find((entry) => entry.variantIds.includes(variantId));
  if (!rule) {
    return { limit: getGiftQuantityForPromo(promo), variantIds: null };
  }
  return { limit: Math.max(1, rule.giftQuantity), variantIds: rule.variantIds };
}

/** Owning promotion rule for a gift tile on a multi-rule card. */
export function getGiftOwnerRuleId(promo: GiftPromotion, variantId: string): string {
  const mapped = promo.variantRuleMap?.[variantId];
  if (mapped) {
    return mapped;
  }
  const rule = getGiftRuleGroups(promo).find((entry) => entry.variantIds.includes(variantId));
  return rule?.promotionRuleId ?? promo.promotionRuleId;
}

export function groupGiftVariantIdsByOwnerRule(
  promo: GiftPromotion,
  variantIds: string[],
): Map<string, string[]> {
  const byOwner = new Map<string, string[]>();
  variantIds.forEach((id) => {
    const owner = getGiftOwnerRuleId(promo, id);
    const list = byOwner.get(owner) ?? [];
    list.push(id);
    byOwner.set(owner, list);
  });
  return byOwner;
}

/**
 * Sibling rules to clear when persisting a multi-rule offer pick.
 * Only rules that previously had a FE selection and now have none — never untouched
 * defaults / auto single gifts under another product (MLC-1830).
 */
export function getMergedGiftRuleIdsToClear(
  promo: GiftPromotion,
  previousVariantIds: string[],
  nextVariantIds: string[],
  allPromos: GiftPromotion[] = [],
): string[] {
  const prevByOwner = groupGiftVariantIdsByOwnerRule(promo, previousVariantIds);
  const nextByOwner = groupGiftVariantIdsByOwnerRule(promo, nextVariantIds);
  const ruleIds = Array.from(
    new Set([promo.promotionRuleId, ...(promo.mergedRuleIds ?? [])].filter(Boolean) as string[]),
  );

  return ruleIds.filter((ruleId) => {
    if ((nextByOwner.get(ruleId)?.length ?? 0) > 0) {
      return false;
    }
    if ((prevByOwner.get(ruleId)?.length ?? 0) === 0) {
      // Never selected in FE this session — leave Saleor default / auto gift alone.
      return false;
    }
    const raw = allPromos.find((entry) => entry.promotionRuleId === ruleId);
    if (raw && isAutoAddedSingleGiftPromo(raw, allPromos)) {
      return false;
    }
    return true;
  });
}

/**
 * Persist picks per owning rule. Updates owners present in variantIds; only clears
 * siblings that previously had a FE selection (never untouched defaults / auto gifts).
 */
export function buildGiftSelectionsByRuleId(
  promo: GiftPromotion,
  variantIds: string[],
  options?: {
    base?: Record<string, string[]>;
    clearCompeting?: string[];
    previousVariantIds?: string[];
    allPromos?: GiftPromotion[];
  },
): Record<string, string[]> {
  const byOwner = groupGiftVariantIdsByOwnerRule(promo, variantIds);
  const next: Record<string, string[]> = { ...(options?.base ?? {}) };

  byOwner.forEach((ids, ownerRuleId) => {
    next[ownerRuleId] = ids;
  });

  getMergedGiftRuleIdsToClear(
    promo,
    options?.previousVariantIds ?? [],
    variantIds,
    options?.allPromos ?? [],
  ).forEach((ruleId) => {
    next[ruleId] = [];
  });

  (options?.clearCompeting ?? []).forEach((ruleId) => {
    next[ruleId] = [];
  });

  return next;
}

/** Flat list of ids from per-rule maps without double-counting the same pick stored under two keys. */
export function mergePersistedRuleSelectionIds(
  selectionsByRuleId: Record<string, string[]> | null | undefined,
  ruleIds: string[],
): string[] {
  const lists = ruleIds
    .map((ruleId) => selectionsByRuleId?.[ruleId])
    .filter((ids): ids is string[] => ids !== undefined);

  if (!lists.length) {
    return [];
  }

  // Per-rule counts, then take the max per variant across rules. That keeps
  // intentional qty>1 on one rule ([g,g]) but drops the card-id + owner-id duplicate ([g]+[g]).
  const maxCountByVariant = new Map<string, number>();
  const order: string[] = [];

  lists.forEach((ids) => {
    const countInRule = new Map<string, number>();
    ids.forEach((id) => {
      if (!id) {
        return;
      }
      countInRule.set(id, (countInRule.get(id) ?? 0) + 1);
      if (!maxCountByVariant.has(id) && !order.includes(id)) {
        order.push(id);
      }
    });
    countInRule.forEach((count, id) => {
      maxCountByVariant.set(id, Math.max(maxCountByVariant.get(id) ?? 0, count));
    });
  });

  const out: string[] = [];
  order.forEach((id) => {
    const count = maxCountByVariant.get(id) ?? 0;
    for (let i = 0; i < count; i += 1) {
      out.push(id);
    }
  });
  return out;
}

/** All rule ids that share one Available Offers card (primary + merged siblings). */
export function getOfferPromoRuleIds(promo: GiftPromotion): string[] {
  return Array.from(
    new Set([promo.promotionRuleId, ...(promo.mergedRuleIds ?? [])].filter(Boolean) as string[]),
  );
}

/**
 * Flat selected ids for an offer card from per-rule local state.
 * Multi-rule cards store picks under each owner rule id — never read only the card primary id.
 */
export function getLocalOfferSelectionIds(
  localSelections: Record<string, string[]>,
  promo: GiftPromotion,
): string[] {
  const ruleIds = getOfferPromoRuleIds(promo);
  const hasPerRule = ruleIds.some((ruleId) => localSelections[ruleId] !== undefined);
  const merged = hasPerRule
    ? mergePersistedRuleSelectionIds(localSelections, ruleIds)
    : localSelections[promo.promotionRuleId] ?? [];

  return sanitizeOfferSelectionIds(promo, merged);
}

/** Split a flat pick list into per-owner keys for local offer UI state. */
export function splitOfferSelectionByOwnerRule(
  promo: GiftPromotion,
  variantIds: string[],
): Record<string, string[]> {
  const byOwner = groupGiftVariantIdsByOwnerRule(promo, variantIds);
  const next: Record<string, string[]> = {};

  if (byOwner.size === 0) {
    next[promo.promotionRuleId] = [...variantIds];
    return next;
  }

  // Only write owners present in the flat list — never blank sibling rules with [].
  byOwner.forEach((ids, ownerRuleId) => {
    next[ownerRuleId] = ids;
  });
  return next;
}

/** Promotions that can appear in Available Offers (cart sidebar + offers page). */
export function getAvailableOfferPromotions(promotions?: GiftPromotion[] | null): GiftPromotion[] {
  const eligible = (promotions ?? []).filter((promo) => (promo.eligibleGiftVariants?.length ?? 0) > 0);

  // Do not fold "buy 1 get 1 free" (auto) into the same card as a multi-gift
  // pick offer just because they share a promotion name. That merge used to turn
  // the free gift into a carousel pick and hide it under the product
  // (QA: auto + multi-gift in one cart).
  const autoSingles: GiftPromotion[] = [];
  const mergeCandidates: GiftPromotion[] = [];
  eligible.forEach((promo) => {
    if (!promo.optedOut && isAutoAddedSingleGiftPromo(promo, eligible)) {
      autoSingles.push(promo);
    } else {
      mergeCandidates.push(promo);
    }
  });

  const mergedByRuleId = new Map<string, GiftPromotion>();

  mergeCandidates.forEach((promo) => {
    const key = promo.promotionName?.trim() || promo.promotionRuleId || "";
    const existing = mergedByRuleId.get(key);

    if (!existing) {
      const variantRuleMap: Record<string, string> = { ...(promo.variantRuleMap ?? {}) };
      (promo.eligibleGiftVariants ?? []).forEach((variant) => {
        if (!variantRuleMap[variant.id]) {
          variantRuleMap[variant.id] = promo.promotionRuleId;
        }
      });

      mergedByRuleId.set(key, {
        ...promo,
        eligibleGiftVariants: [...(promo.eligibleGiftVariants ?? [])],
        eligibleGiftAvailableQuantities: (promo.eligibleGiftVariants ?? []).map(
          (_, idx) => promo.eligibleGiftAvailableQuantities?.[idx] ?? 1,
        ),
        qualifyingCheckoutLineIds: [...(promo.qualifyingCheckoutLineIds ?? [])],
        qualifyingCartVariants: [...(promo.qualifyingCartVariants ?? [])],
        mergedRuleIds: [...(promo.mergedRuleIds ?? [promo.promotionRuleId])],
        variantRuleMap,
        ruleBreakdown: describePromoRules(promo),
      });
      return;
    }

    (promo.mergedRuleIds ?? [promo.promotionRuleId]).forEach((ruleId) => {
      if (!existing.mergedRuleIds?.includes(ruleId)) {
        existing.mergedRuleIds?.push(ruleId);
      }
    });

    describePromoRules(promo).forEach((entry) => {
      if (!existing.ruleBreakdown?.some((known) => known.promotionRuleId === entry.promotionRuleId)) {
        existing.ruleBreakdown?.push(entry);
      }
    });

    (promo.eligibleGiftVariants ?? []).forEach((variant, idx) => {
      if (!existing.eligibleGiftVariants?.some((v) => v.id === variant.id)) {
        existing.eligibleGiftVariants?.push(variant);
        existing.eligibleGiftAvailableQuantities?.push(promo.eligibleGiftAvailableQuantities?.[idx] ?? 1);
        if (existing.variantRuleMap) {
          existing.variantRuleMap[variant.id] = promo.variantRuleMap?.[variant.id] ?? promo.promotionRuleId;
        }
      }
    });

    (promo.qualifyingCheckoutLineIds ?? []).forEach((lineId) => {
      if (!existing.qualifyingCheckoutLineIds?.includes(lineId)) {
        existing.qualifyingCheckoutLineIds?.push(lineId);
      }
    });
    (promo.qualifyingCartVariants ?? []).forEach((variant) => {
      if (!existing.qualifyingCartVariants?.some((v) => v.id === variant.id)) {
        existing.qualifyingCartVariants?.push(variant);
      }
    });

    const incomingSelected = promo.selectedGiftVariants?.length
      ? promo.selectedGiftVariants
      : promo.selectedGiftVariant
        ? [promo.selectedGiftVariant]
        : [];
    if (incomingSelected.length > 0 && promo.optedOut !== true) {
      const combinedSelected = [
        ...(existing.selectedGiftVariants ?? (existing.selectedGiftVariant ? [existing.selectedGiftVariant] : [])),
      ];
      incomingSelected.forEach((selected) => {
        if (selected && !combinedSelected.some((v) => v.id === selected.id)) {
          combinedSelected.push(selected);
        }
      });
      existing.selectedGiftVariants = combinedSelected;
      existing.selectedGiftVariant = existing.selectedGiftVariant || promo.selectedGiftVariant;
    }
    if (promo.optedOut === false) {
      existing.optedOut = false;
    }
    if (promo.requiresSelection === true) {
      existing.requiresSelection = true;
    }
    existing.giftQuantity = Math.max(existing.giftQuantity ?? 1, promo.giftQuantity ?? 1);
  });

  return Array.from(mergedByRuleId.values()).map((promo) => {
    // Drop empty sibling rows so we only split the card when each rule actually
    // has its own gift list.
    const ruleBreakdown = attachRuleVariantIds(promo).filter((entry) => entry.variantIds.length > 0);
    const splitByRule = ruleBreakdown.length > 1;
    // Multi-rule card: "Selected x/y" limit = sum of each rule's gift qty
    // (e.g. get 1 + get 1 → 2). Single-rule cards keep Saleor's giftQuantity as-is —
    // never bump it to selectedCount (that turned separate 5 + 2 promos into a fake 7/7).
    const qty = splitByRule
      ? ruleBreakdown.reduce((acc, entry) => acc + Math.max(1, entry.giftQuantity), 0)
      : Math.max(1, promo.giftQuantity ?? 1);
    const selectedRaw =
      promo.selectedGiftVariants?.length
        ? promo.selectedGiftVariants
        : promo.selectedGiftVariant
          ? [promo.selectedGiftVariant]
          : [];
    const selected = splitByRule
      ? capGiftSelectionPerRule(selectedRaw, ruleBreakdown, (variant) => variant.id)
      : selectedRaw.slice(0, qty);
    const capped =
      selected.length !== selectedRaw.length || promo.giftQuantity !== qty
        ? {
            ...promo,
            giftQuantity: qty,
            selectedGiftVariants: selected,
            selectedGiftVariant: selected[0],
          }
        : promo;
    const withRules = splitByRule
      ? { ...capped, ruleBreakdown }
      : capped.ruleBreakdown
        ? { ...capped, ruleBreakdown: undefined }
        : capped;

    return (withRules.mergedRuleIds?.length ?? 0) > 1 && withRules.selectionMode === "AUTO"
      ? { ...withRules, selectionMode: "PICK_FROM_POOL" as const }
      : withRules;
  }).concat(autoSingles);
}

export function getGiftQuantityForPromo(promo?: GiftPromotion | null): number {
  const qty = promo?.giftQuantity ?? 1;
  return qty > 0 ? qty : 1;
}

/** Selected gift variant IDs for a rule — prefers multi-gift field, falls back to singular.
 * Preserves duplicate IDs so quantity > 1 of the same gift is kept (e.g. [A, A, B]). */
export function getSelectedGiftVariantIds(promo?: GiftPromotion | null): string[] {
  if (!promo || promo.optedOut) {
    return [];
  }

  const multi =
    promo.selectedGiftVariants?.map((v) => v.id).filter((id): id is string => Boolean(id)) ?? [];
  if (multi.length > 0) {
    return multi;
  }

  if (promo.selectedGiftVariant?.id) {
    return [promo.selectedGiftVariant.id];
  }

  // Single free-gift SKU: treat as selected so it appears under the product after add-to-cart
  // even before Saleor marks selectedGiftVariant (MLC-1830).
  if (isAutoAddedSingleGiftPromo(promo)) {
    const soleId = promo.eligibleGiftVariants?.[0]?.id;
    return soleId ? [soleId] : [];
  }

  return [];
}

export function getOfferCardTitle(
  promo: GiftPromotion,
  cartStaticText?: Record<string, string>,
): string {
  if (promo.promotionName?.trim()) {
    return promo.promotionName;
  }

  const qty = getGiftQuantityForPromo(promo);
  if (qty > 1) {
    return cartStaticText?.cart_grab_n_free_gifts_label?.replace("{{count}}", String(qty)) || "";
  }

  return cartStaticText?.cart_grab_your_free_gift_label || "";
}

export type ResolvedGiftSelectionMode = "AUTO" | "PICK_FROM_POOL" | "PICK_ONE_RULE_PACKAGE";

export function resolveGiftSelectionMode(
  promo: GiftPromotion,
  allPromos: GiftPromotion[] = [],
): ResolvedGiftSelectionMode {
  if (
    promo.selectionMode === "AUTO" ||
    promo.selectionMode === "PICK_FROM_POOL" ||
    promo.selectionMode === "PICK_ONE_RULE_PACKAGE"
  ) {
    return promo.selectionMode;
  }

  const groupKey = promo.selectionGroupKey?.trim();
  if (groupKey) {
    const competing = allPromos.filter((p) => (p.selectionGroupKey || "").trim() === groupKey);
    if (competing.length > 1) {
      return "PICK_ONE_RULE_PACKAGE";
    }
  }

  if ((promo.competingPromotionRuleIds?.length ?? 0) > 0) {
    return "PICK_ONE_RULE_PACKAGE";
  }

  const eligibleCount = promo.eligibleGiftVariants?.length ?? 0;
  const qty = getGiftQuantityForPromo(promo);
  const needsPick = promo.requiresSelection === true || eligibleCount > 1 || qty > 1;

  if (!needsPick) {
    return "AUTO";
  }

  return "PICK_FROM_POOL";
}

/** True when there is exactly one free-gift SKU — nest under the paid product, never on offers page.
 * Stays under that product until the qualifying paid line is removed (Magrabi MLC-1830). */
export function isAutoAddedSingleGiftPromo(promo: GiftPromotion, _allPromos: GiftPromotion[] = []): boolean {
  const eligibleCount = promo.eligibleGiftVariants?.length ?? 0;
  if (eligibleCount !== 1) {
    return false;
  }
  // "Get N of the same gift" still uses the offers page quantity stepper.
  if (getGiftQuantityForPromo(promo) > 1) {
    return false;
  }
  return true;
}

/** Free gifts Saleor adds automatically — show under the product, never in the pick carousel. */
export function getAutoAddedGiftVariantIds(promotions?: GiftPromotion[] | null): Set<string> {
  const list = promotions ?? [];
  const ids = new Set<string>();
  list.forEach((promo) => {
    if (promo.optedOut) {
      return;
    }
    if (isAutoAddedSingleGiftPromo(promo, list)) {
      const id = promo.eligibleGiftVariants?.[0]?.id;
      if (id) {
        ids.add(id);
      }
      return;
    }
    // Per Magrabi: a rule with exactly one gift SKU nests under the product even when
    // the same promotion name also has multi-pick rules on the offers page.
    (promo.ruleBreakdown ?? []).forEach((rule) => {
      if ((rule.variantIds?.length ?? 0) === 1 && Math.max(1, rule.giftQuantity) <= 1) {
        ids.add(rule.variantIds[0]);
      }
    });
  });
  return ids;
}

/** One gift SKU with qty > 1 (e.g. get 2 of the same item). Locked selected; shopper only changes qty. */
export function isFixedSameGiftQuantityPromo(promo?: GiftPromotion | null): boolean {
  return (promo?.eligibleGiftVariants?.length ?? 0) === 1 && getGiftQuantityForPromo(promo) > 1;
}

/** Show qty stepper whenever this gift's rule allows more than one pick (same SKU or mix). */
export function shouldShowGiftQuantityStepper(promo: GiftPromotion, variantId: string): boolean {
  return getGiftSelectionScope(promo, variantId).limit > 1;
}

/**
 * Cap picks to each rule's gift quantity. Same SKU may repeat (qty +) up to the limit;
 * multi-rule cards still cannot steal another rule's slots.
 */
export function sanitizeOfferSelectionIds(promo: GiftPromotion, variantIds: string[]): string[] {
  const groups = getGiftRuleGroups(promo);
  if (groups.length > 1) {
    return capGiftSelectionPerRule(variantIds, promo.ruleBreakdown ?? [], (id) => id);
  }
  return variantIds.slice(0, getGiftQuantityForPromo(promo));
}

export function getSelectionGroupKey(promo: GiftPromotion): string {
  if (promo.selectionGroupKey?.trim()) {
    return promo.selectionGroupKey.trim();
  }
  if ((promo.competingPromotionRuleIds?.length ?? 0) > 0) {
    return ["competing", promo.promotionRuleId, ...(promo.competingPromotionRuleIds || [])].sort().join("|");
  }
  return promo.promotionRuleId;
}

export function groupGiftPromotions(promotions?: GiftPromotion[] | null): Array<{
  groupKey: string;
  mode: ResolvedGiftSelectionMode;
  promotions: GiftPromotion[];
}> {
  const offers = getAvailableOfferPromotions(promotions);
  const map = new Map<string, GiftPromotion[]>();

  offers.forEach((promo) => {
    const mode = resolveGiftSelectionMode(promo, offers);
    const key =
      mode === "PICK_ONE_RULE_PACKAGE" ? getSelectionGroupKey(promo) : `solo:${promo.promotionRuleId}`;
    const list = map.get(key) || [];
    list.push(promo);
    map.set(key, list);
  });

  return Array.from(map.entries()).map(([groupKey, promos]) => {
    const mode =
      promos.length > 1
        ? ("PICK_ONE_RULE_PACKAGE" as const)
        : resolveGiftSelectionMode(promos[0], offers);
    return { groupKey, mode, promotions: promos };
  });
}

export function getQualifyingVariantsForPromo(
  promo: GiftPromotion,
  lines?: CheckoutLines[] | null,
): Array<{ id?: string; sku?: string; name?: string }> {
  if (promo.qualifyingCartVariants?.length) {
    return promo.qualifyingCartVariants.map((v) => ({
      id: v.id,
      sku: v.sku != null ? String(v.sku) : undefined,
      name: v.name || v.product?.name,
    }));
  }

  if (promo.qualifyingCheckoutLineIds?.length && lines?.length) {
    return promo.qualifyingCheckoutLineIds
      .map((lineId) => lines.find((l) => l.id === lineId))
      .filter(Boolean)
      .map((line) => ({
        id: line!.variant?.id,
        sku: line!.variant?.sku != null ? String(line!.variant?.sku) : undefined,
        name: line!.variant?.name || line!.variant?.product?.name,
      }));
  }

  return (lines ?? [])
    .filter((line) => !isCheckoutGiftLine(line) && !line.buyGetGiftFlag)
    .filter((line) =>
      (line.appliedPromotions ?? []).some(
        (p) => p.id === promo.promotionRuleId || p.promotion?.name === promo.promotionName,
      ),
    )
    .map((line) => ({
      id: line.variant?.id,
      sku: line.variant?.sku != null ? String(line.variant?.sku) : undefined,
      name: line.variant?.name || line.variant?.product?.name,
    }));
}

export function formatQualifyingSkuLabel(
  promo: GiftPromotion,
  lines?: CheckoutLines[] | null,
  cartStaticText?: Record<string, string>,
): string {
  const variants = getQualifyingVariantsForPromo(promo, lines);
  if (!variants.length) {
    return "";
  }
  const labels = variants.map((v) => v.sku || v.name).filter(Boolean);
  if (!labels.length) {
    return "";
  }
  return (
    cartStaticText?.cart_gift_because_you_bought_label?.replace("{{skus}}", labels.join(", ")) ||
    ""
  );
}

export function getCarouselEligiblePromotions(promotions?: GiftPromotion[] | null): GiftPromotion[] {
  return (promotions ?? []).filter((promo) => (promo.eligibleGiftVariants?.length ?? 0) > 1);
}

export function hasGiftCarouselData(
  checkout?: Pick<Checkout, "availableGiftPromotions" | "availableGifts"> | null,
): boolean {
  if (!checkout) {
    return false;
  }

  if (getCarouselEligiblePromotions(checkout.availableGiftPromotions).length > 0) {
    return true;
  }

  return (checkout.availableGifts?.length ?? 0) > 1;
}

/** @deprecated Prefer hasGiftCarouselData — Magrabi only preserves multi-gift catalogs after coupon. */
export function hasGiftPromotionData(
  checkout?: Pick<Checkout, "availableGiftPromotions" | "availableGifts"> | null,
): boolean {
  if (!checkout) {
    return false;
  }

  if ((checkout.availableGiftPromotions?.length ?? 0) > 0) {
    return true;
  }

  return (checkout.availableGifts?.length ?? 0) > 0;
}

function resolvePreservedAvailableGifts(
  previous?: AvailableGifts[] | null,
  next?: AvailableGifts[] | null,
): AvailableGifts[] | undefined {
  if ((next?.length ?? 0) > 1) {
    return next ?? undefined;
  }

  if ((previous?.length ?? 0) > 1) {
    return previous ?? undefined;
  }

  return next?.length ? next : previous ?? undefined;
}

export function overlayGiftSelectionOnPromotions(
  promotions: GiftPromotion[] | undefined,
  selectionId?: string | null,
): GiftPromotion[] | undefined {
  if (!promotions?.length || !selectionId) {
    return promotions;
  }

  const owningPromo = promotions.find((promo) =>
    promo.eligibleGiftVariants?.some((variant) => variant.id === selectionId),
  );

  if (!owningPromo) {
    return promotions;
  }

  return promotions.map((promo) => {
    if (promo.promotionRuleId !== owningPromo.promotionRuleId) {
      return { ...promo, optedOut: true, selectedGiftVariant: undefined };
    }

    const selectedVariant = promo.eligibleGiftVariants?.find((variant) => variant.id === selectionId);
    return {
      ...promo,
      optedOut: false,
      selectedGiftVariant: selectedVariant,
    };
  });
}

export function overlayGiftSelectionsOnPromotions(
  promotions: GiftPromotion[] | undefined,
  selectionIds: string[],
  ownerPromo?: Pick<GiftPromotion, "promotionRuleId" | "promotionName" | "mergedRuleIds"> | null,
): GiftPromotion[] | undefined {
  if (!promotions?.length) {
    return promotions;
  }

  // Without an owner, never apply a global empty/flat list across every offer.
  if (!ownerPromo) {
    return promotions;
  }

  const ownerRuleIds = new Set<string>(
    [ownerPromo.promotionRuleId, ...(ownerPromo.mergedRuleIds ?? [])].filter(Boolean) as string[],
  );

  const matchesOwner = (promo: GiftPromotion) => {
    if (ownerRuleIds.has(promo.promotionRuleId)) {
      return true;
    }
    if (promo.mergedRuleIds?.some((id) => ownerRuleIds.has(id))) {
      return true;
    }
    // Match by rule id only — not by promotion name. Two offers can share a name
    // (one auto free gift, one pick-your-gift). Name matching used to remove the
    // auto gift under product A when the shopper picked a gift under product B.
    return false;
  };

  return promotions.map((promo) => {
    if (!matchesOwner(promo)) {
      return promo;
    }

    const eligible = promo.eligibleGiftVariants ?? [];
    const limit = Math.max(1, promo.giftQuantity ?? 1);
    // First keep only gifts this rule can give, then apply the qty cap.
    // A merged card may send every sibling rule's picks together — if we truncate
    // before filtering, this rule's own gift can get cut off by another rule's picks.
    const eligibleIds = selectionIds.filter((id) => eligible.some((variant) => variant.id === id));
    const cappedIds = (promo.ruleBreakdown?.length ?? 0) > 1
      ? capGiftSelectionPerRule(eligibleIds, promo.ruleBreakdown ?? [], (id) => id)
      : eligibleIds.slice(0, limit);

    if (selectionIds.length === 0) {
      return {
        ...promo,
        optedOut: true,
        selectedGiftVariant: undefined,
        selectedGiftVariants: [],
      };
    }

    const selectedOnRow = cappedIds
      .map((id) => eligible.find((variant) => variant.id === id))
      .filter(Boolean) as AvailableGifts[];

    if (selectedOnRow.length > 0) {
      return {
        ...promo,
        optedOut: false,
        selectedGiftVariant: selectedOnRow[0],
        selectedGiftVariants: selectedOnRow,
      };
    }

    return {
      ...promo,
      optedOut: false,
      selectedGiftVariant: undefined,
      selectedGiftVariants: [],
    };
  });
}

export function overlaySelectionsByRuleId(
  promotions: GiftPromotion[] | undefined,
  selectionsByRuleId: Record<string, string[]>,
): GiftPromotion[] | undefined {
  if (!promotions?.length || !Object.keys(selectionsByRuleId).length) {
    return promotions;
  }

  return promotions.map((promo) => {
    const directIds = selectionsByRuleId[promo.promotionRuleId];
    if (directIds !== undefined) {
      return overlayGiftSelectionsOnPromotions([promo], directIds, promo)?.[0] ?? promo;
    }

    const siblingKey = (promo.mergedRuleIds ?? []).find((id) => selectionsByRuleId[id] !== undefined);
    if (siblingKey) {
      return overlayGiftSelectionsOnPromotions([promo], selectionsByRuleId[siblingKey] ?? [], promo)?.[0] ?? promo;
    }

    return promo;
  });
}

export function withOptimisticOfferGiftSelection(
  cart: Checkout,
  promo: GiftPromotion,
  variantIds: string[],
): Checkout {
  const cappedIds =
    (promo.ruleBreakdown?.length ?? 0) > 1
      ? capGiftSelectionPerRule(variantIds, promo.ruleBreakdown ?? [], (id) => id)
      : variantIds.slice(0, getGiftQuantityForPromo(promo));
  const nextPromotions = overlayGiftSelectionsOnPromotions(
    cart.availableGiftPromotions,
    cappedIds,
    promo,
  );

  return {
    ...cart,
    availableGiftPromotions: nextPromotions ?? cart.availableGiftPromotions,
    selectedGiftVariant: cappedIds[0] ?? undefined,
  };
}

export function resolveOfferSelectionIds(
  promo: GiftPromotion,
  checkoutId?: string | null,
): string[] {
  const qty = getGiftQuantityForPromo(promo);
  const eligibleIds = new Set((promo.eligibleGiftVariants ?? []).map((variant) => variant.id));

  const fromApi = getSelectedGiftVariantIds(promo).filter((id) => eligibleIds.has(id));

  let fromPersisted: string[] = [];
  let hasExplicitRuleSelection = false;
  if (checkoutId && typeof window !== "undefined") {
    const persisted = readGiftCheckoutSelection(checkoutId);
    // We save picks per rule id. For a multi-rule offer, read every sibling rule
    // and combine them — reading only the first rule forgot gifts picked under the others.
    const ruleIds = Array.from(
      new Set([promo.promotionRuleId, ...(promo.mergedRuleIds ?? [])].filter(Boolean) as string[]),
    );
    const storedPerRule = ruleIds
      .map((ruleId) => persisted?.selectionsByRuleId?.[ruleId])
      .filter((ids): ids is string[] => ids !== undefined);

    // An empty list [] means the shopper opted out — that is intentional, not "no data".
    if (storedPerRule.length > 0) {
      hasExplicitRuleSelection = true;
      fromPersisted = mergePersistedRuleSelectionIds(persisted?.selectionsByRuleId, ruleIds).filter((id) =>
        eligibleIds.has(id),
      );
    } else {
      fromPersisted = (persisted?.selectedGiftVariantIds ?? []).filter((id) => eligibleIds.has(id));
    }
  }

  if (hasExplicitRuleSelection && fromPersisted.length === 0) {
    // Fixed "same gift, qty > 1" offers cannot be fully cleared — keep Saleor's qty selected.
    if (isFixedSameGiftQuantityPromo(promo)) {
      const soleId = promo.eligibleGiftVariants?.[0]?.id;
      return soleId ? Array.from({ length: qty }, () => soleId) : [];
    }
    return [];
  }

  // Prefer live API when it has a selection — session can lag after a successful mutation.
  const preferred =
    fromApi.length > 0
      ? fromApi
      : fromPersisted.length > 0
        ? fromPersisted
        : fromApi;

  if (preferred.length === 0 && isFixedSameGiftQuantityPromo(promo)) {
    const soleId = promo.eligibleGiftVariants?.[0]?.id;
    return soleId ? Array.from({ length: qty }, () => soleId) : [];
  }

  if ((promo.ruleBreakdown?.length ?? 0) > 1) {
    return sanitizeOfferSelectionIds(promo, capGiftSelectionPerRule(preferred, promo.ruleBreakdown ?? [], (id) => id));
  }

  return sanitizeOfferSelectionIds(promo, preferred.slice(0, qty));
}

export function buildOfferLocalSelections(
  promotions: GiftPromotion[],
  checkoutId?: string | null,
): Record<string, string[]> {
  const next: Record<string, string[]> = {};
  promotions.forEach((promo) => {
    Object.assign(next, splitOfferSelectionByOwnerRule(promo, resolveOfferSelectionIds(promo, checkoutId)));
  });
  return next;
}

function resolveCheckoutGiftSelectionId(checkout?: Checkout | null): string | null {
  if (!checkout) {
    return null;
  }

  const explicit =
    checkout.selectedGiftVariant ??
    checkout.availableGiftPromotions?.find((promo) => promo.selectedGiftVariant?.id)?.selectedGiftVariant?.id ??
    null;

  if (explicit) {
    return explicit;
  }

  const promoGifts =
    checkout.availableGiftPromotions?.flatMap((promo) => promo.eligibleGiftVariants ?? []) ?? [];
  if (promoGifts.length === 1 && promoGifts[0]?.id) {
    return promoGifts[0].id;
  }

  if (checkout.availableGifts?.length === 1) {
    return checkout.availableGifts[0].id;
  }

  return null;
}

function giftCheckoutSelectionStorageKey(checkoutId: string): string {
  return `${sessionStorageKeys.GIFT_CHECKOUT_SELECTION}:${checkoutId}`;
}

export function persistGiftCheckoutSelection(
  checkoutId: string,
  selection: PersistedGiftCheckoutSelection,
): void {
  if (typeof window === "undefined" || !checkoutId) {
    return;
  }

  try {
    const previous = readGiftCheckoutSelection(checkoutId);
    const selectionsByRuleId = {
      ...(previous?.selectionsByRuleId ?? {}),
      ...(selection.selectionsByRuleId ?? {}),
    };
    const flatFromRules = Object.values(selectionsByRuleId).flat().filter(Boolean);
    const payload: PersistedGiftCheckoutSelection = {
      ...previous,
      ...selection,
      selectionsByRuleId:
        Object.keys(selectionsByRuleId).length > 0 ? selectionsByRuleId : selection.selectionsByRuleId,
      selectedGiftVariantIds:
        selection.selectedGiftVariantIds ??
        (flatFromRules.length > 0 ? flatFromRules : previous?.selectedGiftVariantIds),
      selectedGiftVariantId:
        selection.selectedGiftVariantId ??
        flatFromRules[0] ??
        previous?.selectedGiftVariantId ??
        null,
    };
    sessionStorage.setItem(giftCheckoutSelectionStorageKey(checkoutId), JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function readGiftCheckoutSelection(checkoutId: string): PersistedGiftCheckoutSelection | null {
  if (typeof window === "undefined" || !checkoutId) {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(giftCheckoutSelectionStorageKey(checkoutId));
    return raw ? (JSON.parse(raw) as PersistedGiftCheckoutSelection) : null;
  } catch {
    return null;
  }
}

export function clearGiftCheckoutSelection(checkoutId: string): void {
  if (typeof window === "undefined" || !checkoutId) {
    return;
  }

  try {
    sessionStorage.removeItem(giftCheckoutSelectionStorageKey(checkoutId));
  } catch {
    /* ignore */
  }
}

/** Drop persisted rule selections that are no longer eligible on the checkout. */
export function pruneStaleGiftCheckoutSelections(
  checkoutId: string,
  activePromotions?: GiftPromotion[] | null,
): PersistedGiftCheckoutSelection | null {
  const persisted = readGiftCheckoutSelection(checkoutId);
  if (!persisted || !checkoutId) {
    return persisted;
  }

  const livePromos = activePromotions ?? [];
  // No gift rules left on the live checkout — drop the whole session snapshot so
  // non-promo carts cannot resurrect BXGY gift cards (MLC-1830).
  if (livePromos.length === 0) {
    try {
      sessionStorage.removeItem(giftCheckoutSelectionStorageKey(checkoutId));
    } catch {
      /* ignore */
    }
    return null;
  }

  if (!persisted.selectionsByRuleId) {
    return persisted;
  }

  const activeRuleIds = new Set<string>();
  livePromos.forEach((promo) => {
    if (promo.promotionRuleId) {
      activeRuleIds.add(promo.promotionRuleId);
    }
    (promo.mergedRuleIds ?? []).forEach((id) => {
      if (id) {
        activeRuleIds.add(id);
      }
    });
  });

  const nextSelections: Record<string, string[]> = {};
  let changed = false;
  Object.entries(persisted.selectionsByRuleId).forEach(([ruleId, ids]) => {
    if (activeRuleIds.has(ruleId)) {
      nextSelections[ruleId] = ids;
    } else {
      changed = true;
    }
  });

  if (!changed) {
    return persisted;
  }

  const flat = Object.values(nextSelections).flat().filter(Boolean);
  // Drop snapshotted promo catalogs when the live checkout no longer has those rules —
  // otherwise empty-cart / non-promo carts resurrect stale BXGY gifts (MLC-1830).
  const nextPayload: PersistedGiftCheckoutSelection = {
    ...persisted,
    selectionsByRuleId: nextSelections,
    selectedGiftVariantIds: flat.length > 0 ? flat : undefined,
    selectedGiftVariantId: flat[0] ?? null,
    availableGiftPromotions: livePromos,
    availableGifts: persisted.availableGifts,
  };

  try {
    sessionStorage.setItem(giftCheckoutSelectionStorageKey(checkoutId), JSON.stringify(nextPayload));
  } catch {
    /* ignore */
  }

  return nextPayload;
}

/**
 * Only re-apply session selections when API already has a selection / opt-out,
 * or gift lines for those variants exist. Prevents remove→re-add resurrecting gifts.
 */
function filterPersistedSelectionsForOverlay(
  selectionsByRuleId: Record<string, string[]>,
  promotions: GiftPromotion[] | null | undefined,
  lines?: CheckoutLines[] | null,
): Record<string, string[]> {
  const filtered: Record<string, string[]> = {};
  const lineVariantIds = new Set(
    (lines ?? [])
      .map((line) => line.variant?.id)
      .filter((id): id is string => Boolean(id)),
  );

  (promotions ?? []).forEach((promo) => {
    const persistedIds = selectionsByRuleId[promo.promotionRuleId];
    if (persistedIds === undefined) {
      return;
    }

    const apiIds = getSelectedGiftVariantIds(promo);
    if (promo.optedOut || persistedIds.length === 0) {
      filtered[promo.promotionRuleId] = persistedIds;
      return;
    }

    if (apiIds.length > 0) {
      filtered[promo.promotionRuleId] = persistedIds;
      return;
    }

    if (persistedIds.some((id) => lineVariantIds.has(id))) {
      filtered[promo.promotionRuleId] = persistedIds;
    }
  });

  return filtered;
}

export function buildCheckoutGiftContext(
  checkout: Pick<
    Checkout,
    | "id"
    | "lines"
    | "availableGiftPromotions"
    | "availableGifts"
    | "selectedGiftVariant"
    | "totalPrice"
    | "subtotalPrice"
  >,
  options?: {
    enrichedGiftCatalog?: Record<string, AvailableGifts> | null;
    persistedSelection?: PersistedGiftCheckoutSelection | null;
  },
): GiftCheckoutContext {
  // Explicit null means "not hydrated yet" — never fall through to window storage.
  const persisted = options?.persistedSelection ?? null;

  // Trust a live array (including []) so clear-cart / non-promo carts do not
  // resurrect BXGY gift catalogs from sessionStorage (MLC-1830).
  const livePromos = checkout.availableGiftPromotions;
  let availableGiftPromotions =
    livePromos !== undefined && livePromos !== null
      ? livePromos
      : (persisted?.availableGiftPromotions ?? livePromos);

  if (persisted?.selectionsByRuleId && Object.keys(persisted.selectionsByRuleId).length > 0) {
    const safeSelections = filterPersistedSelectionsForOverlay(
      persisted.selectionsByRuleId,
      availableGiftPromotions,
      checkout.lines,
    );
    if (Object.keys(safeSelections).length > 0) {
      availableGiftPromotions =
        overlaySelectionsByRuleId(availableGiftPromotions ?? undefined, safeSelections) ??
        availableGiftPromotions;
    }
  }

  // Selections are applied via overlay above when safe; never fall back to a bare
  // session selectedGiftVariantId — that resurrects gifts on non-promo carts.
  const selectionId =
    availableGiftPromotions
      ?.flatMap((promo) => getSelectedGiftVariantIds(promo))
      .find(Boolean) ??
    resolveCheckoutGiftSelectionId(checkout as Checkout) ??
    null;

  const liveGifts = checkout.availableGifts;
  const availableGifts =
    liveGifts !== undefined && liveGifts !== null
      ? liveGifts
      : (persisted?.availableGifts ?? liveGifts);

  return {
    lines: checkout.lines,
    availableGiftPromotions,
    availableGifts,
    selectedGiftVariant: selectionId ?? undefined,
    currency:
      checkout.totalPrice?.gross?.currency ?? checkout.subtotalPrice?.gross?.currency ?? "",
    enrichedGiftCatalog: options?.enrichedGiftCatalog,
    allowSyntheticGifts: true,
  };
}

export function getCheckoutGiftSelectionIds(checkout?: Checkout | null): string[] {
  if (!checkout) {
    return [];
  }

  const ids: string[] = [];
  getAvailableOfferPromotions(checkout.availableGiftPromotions).forEach((promo) => {
    ids.push(...getSelectedGiftVariantIds(promo));
  });

  if (!ids.length) {
    checkout.availableGiftPromotions?.forEach((promo) => {
      ids.push(...getSelectedGiftVariantIds(promo));
    });
  }

  if (!ids.length && checkout.selectedGiftVariant) {
    ids.push(checkout.selectedGiftVariant);
  }

  return ids;
}

export function mergeCheckoutPreservingGiftSelection(
  previous: Checkout | undefined,
  next: Checkout,
): Checkout {
  // Prefer API promotions whenever present (including empty) so clear-cart / non-promo
  // products do not resurrect stale BXGY gifts from previous React state or sessionStorage.
  const nextPromos = Array.isArray(next.availableGiftPromotions) ? next.availableGiftPromotions : null;
  let persisted =
    typeof window !== "undefined" && next.id ? readGiftCheckoutSelection(next.id) : null;

  if (typeof window !== "undefined" && next.id && nextPromos !== null) {
    persisted = pruneStaleGiftCheckoutSelections(next.id, nextPromos);
  }

  const hasPromos = (promos?: GiftPromotion[] | null) => (promos?.length ?? 0) > 0;

  let availableGiftPromotions =
    nextPromos !== null
      ? nextPromos
      : hasPromos(previous?.availableGiftPromotions)
        ? previous?.availableGiftPromotions
        : persisted?.availableGiftPromotions;

  if (persisted?.selectionsByRuleId && Object.keys(persisted.selectionsByRuleId).length > 0) {
    const safeSelections = filterPersistedSelectionsForOverlay(
      persisted.selectionsByRuleId,
      availableGiftPromotions,
      next.lines,
    );
    if (Object.keys(safeSelections).length > 0) {
      availableGiftPromotions =
        overlaySelectionsByRuleId(availableGiftPromotions ?? undefined, safeSelections) ??
        availableGiftPromotions;
    }
  }

  const selectionIds = getCheckoutGiftSelectionIds({
    ...next,
    availableGiftPromotions: availableGiftPromotions ?? undefined,
  });
  const selectionId =
    selectionIds[0] ??
    resolveCheckoutGiftSelectionId(next) ??
    (selectionIds.length > 0 ? persisted?.selectedGiftVariantId : null) ??
    resolveCheckoutGiftSelectionId(previous) ??
    null;

  const availableGifts =
    Array.isArray(next.availableGifts)
      ? next.availableGifts
      : (previous?.availableGifts?.length ?? 0) > 0
        ? previous?.availableGifts
        : persisted?.availableGifts;

  return {
    ...next,
    selectedGiftVariant: selectionId ?? next.selectedGiftVariant,
    availableGiftPromotions: availableGiftPromotions ?? undefined,
    availableGifts: availableGifts ?? undefined,
  };
}

export function getVisibleCheckoutLineCount(
  checkout: Pick<
    Checkout,
    | "id"
    | "lines"
    | "availableGiftPromotions"
    | "availableGifts"
    | "selectedGiftVariant"
    | "totalPrice"
    | "subtotalPrice"
  >,
  options?: {
    enrichedGiftCatalog?: Record<string, AvailableGifts> | null;
    persistedSelection?: PersistedGiftCheckoutSelection | null;
  },
): number {
  return getVisibleGiftCheckoutLines(buildCheckoutGiftContext(checkout, options)).length;
}

export function mergeCartPreservingGiftPromotions(previous: Checkout | undefined, next: Checkout): Checkout {
  const withSelection = mergeCheckoutPreservingGiftSelection(previous, next);

  if (!previous) {
    return withSelection;
  }

  const shouldPreserveGiftCarousel = Boolean(withSelection.voucherCode) && hasGiftCarouselData(previous);

  if (shouldPreserveGiftCarousel) {
    const preservedPromotions = (previous.availableGiftPromotions ?? []).map((prevPromo) => {
      const nextPromo = withSelection.availableGiftPromotions?.find(
        (item) => item.promotionRuleId === prevPromo.promotionRuleId,
      );
      return mergeGiftPromotionDetails(prevPromo, nextPromo) ?? prevPromo;
    });

    (previous.availableGiftPromotions ?? []).forEach((prevPromo) => {
      if (!preservedPromotions.some((promo) => promo.promotionRuleId === prevPromo.promotionRuleId)) {
        preservedPromotions.push(prevPromo);
      }
    });

    const selectionIds = getCheckoutGiftSelectionIds(withSelection);
    const persisted =
      typeof window !== "undefined" && withSelection.id
        ? readGiftCheckoutSelection(withSelection.id)
        : null;
    const overlayed =
      persisted?.selectionsByRuleId && Object.keys(persisted.selectionsByRuleId).length > 0
        ? overlaySelectionsByRuleId(preservedPromotions, persisted.selectionsByRuleId)
        : preservedPromotions;

    return {
      ...withSelection,
      availableGiftPromotions: overlayed ?? preservedPromotions,
      availableGifts: resolvePreservedAvailableGifts(previous.availableGifts, withSelection.availableGifts),
      selectedGiftVariant: selectionIds[0] ?? withSelection.selectedGiftVariant,
    };
  }

  if (previous.availableGiftPromotions?.length && withSelection.availableGiftPromotions?.length) {
    const mergedPromotions = withSelection.availableGiftPromotions.map((promo) => {
      const previousPromo = previous.availableGiftPromotions?.find(
        (item) => item.promotionRuleId === promo.promotionRuleId,
      );
      return mergeGiftPromotionDetails(previousPromo, promo) ?? promo;
    });

    return {
      ...withSelection,
      availableGiftPromotions: mergedPromotions,
      availableGifts: resolvePreservedAvailableGifts(previous.availableGifts, withSelection.availableGifts),
    };
  }

  return withSelection;
}

/** Lens add-ons only (excludes free-gift children). */
export function getLensAddOns(line?: CheckoutLines | null): CheckoutLines[] {
  return (line?.addOns ?? []).filter((addOn) => !isCheckoutGiftLine(addOn));
}

export function getGiftAddOns(line?: CheckoutLines | null): CheckoutLines[] {
  return (line?.addOns ?? []).filter((addOn) => isCheckoutGiftLine(addOn) || Boolean(addOn.buyGetGiftFlag));
}

/**
 * Free gifts must render as full cart cards (never nested rows), and all paid
 * merchandise stays above the free-gift block.
 */
export function promoteGiftAddOnsToSiblingLines(lines: CheckoutLines[]): CheckoutLines[] {
  if (!lines.length) {
    return lines;
  }

  const paid: CheckoutLines[] = [];
  const gifts: CheckoutLines[] = [];
  const seenGiftKeys = new Set<string>();

  const giftKey = (line: CheckoutLines) => line.id || line.variant?.id || '';
  const markGift = (line: CheckoutLines) => {
    const key = giftKey(line);
    if (key && seenGiftKeys.has(key)) {
      return false;
    }
    if (key) {
      seenGiftKeys.add(key);
    }
    return true;
  };

  lines.forEach((line) => {
    const giftAddOns = getGiftAddOns(line);
    const otherAddOns = getLensAddOns(line);
    const normalized = giftAddOns.length ? { ...line, addOns: otherAddOns } : line;

    if (isCheckoutGiftLine(normalized) || Boolean(normalized.buyGetGiftFlag)) {
      if (markGift(normalized)) {
        gifts.push(normalized);
      }
    } else {
      paid.push(normalized);
    }

    giftAddOns.forEach((gift) => {
      const promoted = {
        ...gift,
        parent: gift.parent?.id ? gift.parent : { id: line.id },
        addOns: [] as CheckoutLines[],
      };
      if (markGift(promoted)) {
        gifts.push(promoted);
      }
    });
  });

  return [...paid, ...gifts];
}

function flattenCheckoutLinesForMerge(lines: CheckoutLines[]): CheckoutLines[] {
  const flat: CheckoutLines[] = [];

  lines.forEach((line) => {
    const nestedGifts = (line.addOns ?? []).filter(
      (addOn) => isCheckoutGiftLine(addOn) || Boolean(addOn.buyGetGiftFlag),
    );
    const otherAddOns = (line.addOns ?? []).filter(
      (addOn) => !isCheckoutGiftLine(addOn) && !addOn.buyGetGiftFlag,
    );

    // Clear nested addOns — both lens and gift children are re-attached by mergeCheckoutLines.
    // Lens addOns must be flattened too; otherwise a second merge (e.g. getVisibleGiftCheckoutLines
    // after CartItems already merged) wipes them via `addOns: []` and never re-nests them.
    flat.push({ ...line, addOns: [] as CheckoutLines[] });

    otherAddOns.forEach((addOn) => {
      flat.push({
        ...addOn,
        parent: addOn.parent?.id ? addOn.parent : { id: line.id },
        addOns: [] as CheckoutLines[],
      });
    });

    nestedGifts.forEach((gift) => {
      flat.push({
        ...gift,
        parent: gift.parent?.id ? gift.parent : { id: line.id },
        addOns: [] as CheckoutLines[],
      });
    });
  });

  return flat;
}

function findNestParentForGift(
  gift: CheckoutLines,
  parents: CheckoutLines[],
): CheckoutLines | null {
  if (!parents.length) {
    return null;
  }

  const parentFromLink = gift.parent?.id
    ? parents.find((parent) => parent.id === gift.parent?.id)
    : undefined;
  if (parentFromLink) {
    return parentFromLink;
  }

  const giftPromoIds = new Set(
    (gift.appliedPromotions ?? []).map((promo) => promo.id).filter(Boolean),
  );
  const giftPromoNames = new Set(
    (gift.appliedPromotions ?? [])
      .map((promo) => promo.promotion?.name?.trim())
      .filter(Boolean) as string[],
  );

  const byPromo = parents.find((parent) =>
    (parent.appliedPromotions ?? []).some((promo) => {
      if (giftPromoIds.has(promo.id)) {
        return true;
      }
      const name = promo.promotion?.name?.trim();
      return Boolean(name && giftPromoNames.has(name));
    }),
  );
  if (byPromo) {
    return byPromo;
  }

  const bxgyParents = parents.filter((parent) =>
    (parent.appliedPromotions ?? []).some(
      (promo) =>
        promo.giftType === giftTypes.FREE_GIFT ||
        promo.promotion?.type === promotionType.BUYXGETY,
    ),
  );
  if (bxgyParents.length === 1) {
    return bxgyParents[0];
  }

  if (parents.length === 1) {
    return parents[0];
  }

  return null;
}

function pushUniqueGiftSibling(
  giftsByParentId: Record<string, CheckoutLines[]>,
  parentId: string,
  gift: CheckoutLines,
): void {
  if (!giftsByParentId[parentId]) {
    giftsByParentId[parentId] = [];
  }
  const alreadyListed = giftsByParentId[parentId].some(
    (existing) =>
      existing.id === gift.id ||
      (Boolean(gift.variant?.id) && existing.variant?.id === gift.variant?.id),
  );
  if (alreadyListed) {
    return;
  }
  giftsByParentId[parentId].push({
    ...gift,
    parent: { id: parentId },
    addOns: [] as CheckoutLines[],
  });
}

export function mergeCheckoutLines(lines: CheckoutLines[]): CheckoutLines[] {
  const flatLines = flattenCheckoutLinesForMerge(lines);
  const addOns: CheckoutLines[] = [];
  const giftChildren: CheckoutLines[] = [];
  const rootProducts: CheckoutLines[] = [];
  const productMap: Record<string, CheckoutLines> = {};

  flatLines.forEach((line) => {
    if (line?.parent?.id) {
      if (isCheckoutGiftLine(line) || line.buyGetGiftFlag) {
        giftChildren.push({ ...line, addOns: [] as CheckoutLines[] });
        return;
      }

      addOns.push(line);
      return;
    }

    const productItem = { ...line, addOns: [] as CheckoutLines[] };
    productMap[line.id] = productItem;
    rootProducts.push(productItem);
  });

  addOns.forEach((addOn) => {
    const parentId = addOn.parent?.id;
    if (parentId && productMap[parentId]) {
      productMap[parentId].addOns?.push(addOn);
      return;
    }

    const orphanLine = { ...addOn, parent: { id: "" }, addOns: [] as CheckoutLines[] };
    productMap[orphanLine.id] = orphanLine;
    rootProducts.push(orphanLine);
  });

  const orphanGifts: CheckoutLines[] = [];
  const giftsByParentId: Record<string, CheckoutLines[]> = {};
  giftChildren.forEach((gift) => {
    const parentId = gift.parent?.id;
    if (parentId && productMap[parentId]) {
      pushUniqueGiftSibling(giftsByParentId, parentId, gift);
    } else {
      orphanGifts.push(gift);
    }
  });

  const regularItems = rootProducts.filter((item) => !isCheckoutGiftLine(item) && !item.buyGetGiftFlag);
  const rootGiftItems = rootProducts.filter((item) => isCheckoutGiftLine(item) || item.buyGetGiftFlag);

  const stillOrphan: CheckoutLines[] = [];
  [...rootGiftItems, ...orphanGifts].forEach((gift) => {
    const parent = findNestParentForGift(gift, regularItems);
    if (parent) {
      pushUniqueGiftSibling(giftsByParentId, parent.id, gift);
    } else {
      stillOrphan.push(gift);
    }
  });

  const ordered: CheckoutLines[] = [];
  regularItems.forEach((item) => {
    ordered.push(item);
  });
  // All paid merchandise first, then free gifts under/after the list.
  regularItems.forEach((item) => {
    (giftsByParentId[item.id] ?? []).forEach((gift) => ordered.push(gift));
  });

  return [...ordered, ...stillOrphan];
}

function lineMatchesGiftPromo(line: CheckoutLines, promo: GiftPromotion): boolean {
  const ruleIds = new Set(
    [promo.promotionRuleId, ...(promo.mergedRuleIds ?? [])].filter(Boolean),
  );
  const promoName = promo.promotionName?.trim();

  const fromApplied = (line.appliedPromotions ?? []).some((applied) => {
    if (applied.id && ruleIds.has(applied.id as string)) {
      return true;
    }
    return Boolean(promoName && applied.promotion?.name?.trim() === promoName);
  });
  if (fromApplied) {
    return true;
  }

  // Cart lines often only expose the badge via variant.availablePromotions.
  return (line.variant?.availablePromotions ?? []).some((available) => {
    const name = available?.promotion?.name?.trim();
    return Boolean(promoName && name === promoName);
  });
}

function resolvePromoParentTargets(
  promo: GiftPromotion,
  lines: CheckoutLines[] | null | undefined,
): { parentLineIds: string[]; parentVariantIds: string[] } {
  const paidLines = (lines ?? []).filter(
    (line) => !isCheckoutGiftLine(line) && !line.buyGetGiftFlag,
  );

  if (promo.qualifyingCheckoutLineIds?.length) {
    const knownIds = new Set(paidLines.map((line) => line.id));
    const parentLineIds = promo.qualifyingCheckoutLineIds.filter((id) => knownIds.has(id));
    if (parentLineIds.length) {
      return {
        parentLineIds,
        parentVariantIds: (promo.qualifyingCartVariants ?? [])
          .map((variant) => variant.id)
          .filter(Boolean) as string[],
      };
    }
  }

  if (promo.qualifyingCartVariants?.length) {
    const variantIds = promo.qualifyingCartVariants.map((variant) => variant.id).filter(Boolean) as string[];
    const lineIds = paidLines
      .filter((line) => line.variant?.id && variantIds.includes(line.variant.id))
      .map((line) => line.id);
    if (lineIds.length) {
      return { parentLineIds: lineIds, parentVariantIds: variantIds };
    }
  }

  const byPromo = paidLines.filter((line) => lineMatchesGiftPromo(line, promo));

  if (byPromo.length) {
    return {
      parentLineIds: byPromo.map((line) => line.id),
      parentVariantIds: byPromo.map((line) => line.variant?.id).filter(Boolean) as string[],
    };
  }

  if (paidLines.length === 1) {
    return {
      parentLineIds: [paidLines[0].id],
      parentVariantIds: paidLines[0].variant?.id ? [paidLines[0].variant.id] : [],
    };
  }

  return { parentLineIds: [], parentVariantIds: [] };
}

/** Map each gift variant to the paid cart line that unlocked its rule. */
function buildGiftVariantParentLineMap(
  promotions: GiftPromotion[] | null | undefined,
  lines: CheckoutLines[] | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  const paidLines = (lines ?? []).filter(
    (line) => !isCheckoutGiftLine(line) && !line.buyGetGiftFlag,
  );
  const rawPromos = promotions ?? [];

  // Same promo name can have several rules, each unlocking a different paid line.
  // When Saleor has not sent qualifyingCheckoutLineIds, match each rule to a parent
  // uniquely (qualifying variants or applied rule id) — never zip by cart index.
  const peerParentByRuleId = new Map<string, string>();
  const promosByName = new Map<string, GiftPromotion[]>();
  rawPromos.forEach((promo) => {
    const name = promo.promotionName?.trim() || promo.promotionRuleId;
    const list = promosByName.get(name) ?? [];
    list.push(promo);
    promosByName.set(name, list);
  });
  promosByName.forEach((peers) => {
    if (peers.length < 2) {
      return;
    }
    if (peers.some((promo) => (promo.qualifyingCheckoutLineIds?.length ?? 0) > 0)) {
      return;
    }

    const claimedLineIds = new Set<string>();

    peers.forEach((promo) => {
      const qualifyingVariantIds = new Set(
        (promo.qualifyingCartVariants ?? []).map((variant) => variant.id).filter(Boolean) as string[],
      );
      if (qualifyingVariantIds.size > 0) {
        const matches = paidLines.filter(
          (line) =>
            !claimedLineIds.has(line.id) &&
            Boolean(line.variant?.id && qualifyingVariantIds.has(line.variant.id)),
        );
        if (matches.length === 1) {
          peerParentByRuleId.set(promo.promotionRuleId, matches[0].id);
          claimedLineIds.add(matches[0].id);
        }
        return;
      }

      const matchesByRuleId = paidLines.filter((line) => {
        if (claimedLineIds.has(line.id)) {
          return false;
        }
        return (line.appliedPromotions ?? []).some((applied) => applied.id === promo.promotionRuleId);
      });
      if (matchesByRuleId.length === 1) {
        peerParentByRuleId.set(promo.promotionRuleId, matchesByRuleId[0].id);
        claimedLineIds.add(matchesByRuleId[0].id);
      }
    });
  });

  rawPromos.forEach((promo) => {
    const peerParentId = peerParentByRuleId.get(promo.promotionRuleId);
    const { parentLineIds } = resolvePromoParentTargets(promo, lines);
    const parentId = peerParentId ?? parentLineIds[0];
    if (!parentId) {
      return;
    }
    (promo.eligibleGiftVariants ?? []).forEach((variant) => {
      if (variant?.id && !map.has(variant.id)) {
        map.set(variant.id, parentId);
      }
    });
    (promo.selectedGiftVariants ?? []).forEach((variant) => {
      if (variant?.id && !map.has(variant.id)) {
        map.set(variant.id, parentId);
      }
    });
    if (promo.selectedGiftVariant?.id && !map.has(promo.selectedGiftVariant.id)) {
      map.set(promo.selectedGiftVariant.id, parentId);
    }
  });
  return map;
}

/** Keep each free gift directly under the paid product that earned it. */
function nestGiftsUnderQualifyingParents(
  lines: CheckoutLines[],
  promotions?: GiftPromotion[] | null,
): CheckoutLines[] {
  if (!lines.length) {
    return lines;
  }

  const parentByGiftVariantId = buildGiftVariantParentLineMap(promotions, lines);
  const paid: CheckoutLines[] = [];
  const gifts: CheckoutLines[] = [];
  const other: CheckoutLines[] = [];

  lines.forEach((line) => {
    if (isCheckoutGiftLine(line) || line.buyGetGiftFlag) {
      gifts.push(line);
    } else if (!line.parent?.id) {
      paid.push(line);
    } else {
      other.push(line);
    }
  });

  if (!gifts.length) {
    return lines;
  }

  const giftsByParentId: Record<string, CheckoutLines[]> = {};
  const orphanGifts: CheckoutLines[] = [];

  gifts.forEach((gift) => {
    const mappedParentId = gift.variant?.id ? parentByGiftVariantId.get(gift.variant.id) : undefined;
    const parentId =
      (mappedParentId && paid.some((line) => line.id === mappedParentId) ? mappedParentId : undefined) ||
      (gift.parent?.id && paid.some((line) => line.id === gift.parent?.id) ? gift.parent.id : undefined);

    if (!parentId) {
      orphanGifts.push(gift);
      return;
    }

    if (!giftsByParentId[parentId]) {
      giftsByParentId[parentId] = [];
    }
    if (!giftsByParentId[parentId].some((existing) => existing.id === gift.id)) {
      giftsByParentId[parentId].push({ ...gift, parent: { id: parentId } });
    }
  });

  const ordered: CheckoutLines[] = [];
  paid.forEach((line) => {
    ordered.push(line);
  });
  // All paid merchandise first, then free gifts (DoctorM / Magrabi cart parity).
  paid.forEach((line) => {
    (giftsByParentId[line.id] ?? []).forEach((gift) => ordered.push(gift));
  });

  return [...ordered, ...other, ...orphanGifts];
}

export function findBxgyTriggerLineId(lines: CheckoutLines[]): string | null {
  const trigger = lines.find(
    (line) =>
      !isCheckoutGiftLine(line) &&
      line.appliedPromotions?.some((promo) => promo.promotion?.type === promotionType.BUYXGETY),
  );

  return trigger?.id ?? null;
}

export function checkoutLineFromAvailableGift(
  gift: AvailableGifts,
  currency: string,
): CheckoutLines {
  const zeroMoney = { amount: 0, currency };
  const mkPrice = () =>
    ({
      currency,
      gross: zeroMoney,
      net: zeroMoney,
      tax: zeroMoney,
    }) as CheckoutLines["totalPrice"];

  const pricing = {
    costPrice: mkPrice(),
    discount: mkPrice(),
    priceUndiscounted: mkPrice(),
    price: mkPrice(),
  };

  const product = gift.product;
  const variantAttributes = gift.attributes ?? [];
  const productAttributes = product?.attributes ?? [];
  const productTypeSlug =
    product?.productType?.slug || inferProductTypeSlugFromGiftAttributes(variantAttributes, productAttributes);
  const displayName = resolveGiftVariantDisplayName(gift);

  return {
    id: `available-gift-${gift.id}`,
    parent: { id: "" },
    metadata: [],
    quantity: 1,
    problems: [],
    totalPrice: mkPrice(),
    unitPrice: mkPrice(),
    appliedPromotions: [],
    isGift: false,
    isBuyxGetyGift: true,
    variant: {
      id: gift.id,
      name: displayName,
      quantityAvailable: 1,
      attributes: variantAttributes,
      sku: (gift as any).sku ?? "",
      landingPageBaseUrl: gift.landingPageBaseUrl,
      product: {
        id: (product as any)?.id ?? `product-${gift.id}`,
        name: product?.name ?? displayName,
        slug: product?.slug,
        productType: { id: "", name: "", slug: productTypeSlug },
        category: { name: "" },
        brand: product?.brand ?? { name: "" },
        attributes: productAttributes,
        translation: product?.translation,
        media: product?.media,
      },
      pricing,
    },
    addOns: [],
  } as CheckoutLines;
}

/** Instantly reflect gift selection in cart state before the API round-trip completes. */
export function withOptimisticGiftSelection(
  cart: Checkout,
  promotionRuleId: string,
  targetVariantId: string | null,
  options?: { clearOtherRules?: boolean; variantIds?: string[] | null },
): Checkout {
  const clearOtherRules = options?.clearOtherRules ?? false;
  const multiIds = options?.variantIds;

  if (!cart.availableGiftPromotions?.length) {
    return {
      ...cart,
      selectedGiftVariant: multiIds?.[0] ?? targetVariantId ?? cart.selectedGiftVariant,
    };
  }

  return {
    ...cart,
    availableGiftPromotions: cart.availableGiftPromotions.map((promo) => {
      if (clearOtherRules && (multiIds?.length || targetVariantId) && promo.promotionRuleId !== promotionRuleId) {
        return {
          ...promo,
          optedOut: true,
          selectedGiftVariant: undefined,
          selectedGiftVariants: undefined,
        };
      }

      if (promo.promotionRuleId !== promotionRuleId) {
        return promo;
      }

      if (multiIds) {
        const selectedVariants = (promo.eligibleGiftVariants || []).filter((variant) =>
          multiIds.includes(variant.id),
        );
        return {
          ...promo,
          optedOut: selectedVariants.length === 0,
          selectedGiftVariant: selectedVariants[0],
          selectedGiftVariants: selectedVariants,
        };
      }

      const selectedVariant = targetVariantId
        ? promo.eligibleGiftVariants?.find((variant) => variant.id === targetVariantId)
        : undefined;

      return {
        ...promo,
        optedOut: !targetVariantId,
        selectedGiftVariant: selectedVariant,
        selectedGiftVariants: selectedVariant ? [selectedVariant] : [],
      };
    }),
    selectedGiftVariant: multiIds?.[0] ?? targetVariantId ?? undefined,
  };
}

/** Order snapshots may omit gift flags — detect zero-price BXGY gift lines for summary display. */
function isZeroPriceBxgyGiftLine(line?: CheckoutLines | null): boolean {
  if (!line) {
    return false;
  }

  const total = getCheckoutPriceAmount(line?.totalPrice) ?? 0;
  if (total > 0.01) {
    return false;
  }

  if (line.isBuyxGetyGift || line.isGift || line.buyGetGiftFlag) {
    return true;
  }

  return (line.appliedPromotions ?? []).some(
    (promo) =>
      promo.giftType === giftTypes.FREE_GIFT || promo.promotion?.type === promotionType.BUYXGETY,
  );
}

export function hasBxgyFreeGiftProductLines(lines?: CheckoutLines[] | null): boolean {
  return (lines ?? []).some((line) => isCheckoutGiftLine(line) || isZeroPriceBxgyGiftLine(line));
}

/** Order success item count — paid merchandise plus displayed free BXGY gifts. */
export function getOrderConfirmationItemCount(lines?: CheckoutLines[] | null): number {
  return (lines ?? []).reduce((total, line) => {
    if (line?.parent?.id) {
      return total;
    }

    const quantity = line.quantity || 1;

    if (isPaidCheckoutMerchandiseLine(line)) {
      return total + quantity;
    }

    if (isCheckoutGiftLine(line) || isZeroPriceBxgyGiftLine(line)) {
      return total + quantity;
    }

    return total + quantity;
  }, 0);
}

function hasBxgyGiftLinesForSubtotalSummary(lines?: CheckoutLines[] | null): boolean {
  return (lines ?? []).some((line) => {
    if (isPaidCheckoutMerchandiseLine(line)) {
      return false;
    }
    return isCheckoutGiftLine(line) || isZeroPriceBxgyGiftLine(line);
  });
}

/** Prefer backend total unless AE voucher tax left VAT residue on gross (list − coupon is the catalog total). */
function resolveDisplayOrderTotal(
  backendOrderTotal: number,
  displayAmount: number,
  discountAmount: number,
  voucherCode?: string | null,
  backendSubtotal?: number,
  paidMerchandiseGross?: number,
): number {
  const derived = Math.max(0, displayAmount - Math.max(0, discountAmount));
  if (voucherCode && discountAmount > 0.01 && displayAmount > 0.01) {
    const paid = paidMerchandiseGross ?? backendSubtotal ?? backendOrderTotal;
    const patched = resolveCatalogVoucherPaidTotal(displayAmount, discountAmount, paid);
    const shippingExtras =
      backendOrderTotal > 0.01 && (backendSubtotal ?? 0) > 0.01
        ? Math.max(0, backendOrderTotal - (backendSubtotal ?? 0))
        : 0;
    if (patched + 0.01 < paid) {
      return patched + shippingExtras;
    }
  }

  if (backendOrderTotal > 0.01) {
    return backendOrderTotal;
  }

  return derived;
}

/**
 * Gift packaging is itemized as its own summary row. Saleor sometimes already includes it in
 * totalPrice (checkout) and sometimes omits it (cart right after toggle) — only add the fee when missing.
 */
function resolveOrderTotalWithGiftWrap(
  merchandiseOrderTotal: number,
  paidSubtotalGross: number,
  giftWrapNet: number,
): number {
  if (giftWrapNet <= 0.01 || merchandiseOrderTotal < 0) {
    return merchandiseOrderTotal;
  }

  // Backend total ≈ paid merchandise + gift wrap → already included.
  if (
    paidSubtotalGross > 0.01 &&
    Math.abs(merchandiseOrderTotal - (paidSubtotalGross + giftWrapNet)) < 0.02
  ) {
    return merchandiseOrderTotal;
  }

  // Backend total ≈ paid merchandise only (or merchandise after discounts) → gift omitted.
  if (paidSubtotalGross > 0.01 && merchandiseOrderTotal <= paidSubtotalGross + 0.02) {
    return merchandiseOrderTotal + giftWrapNet;
  }

  // No reliable paid subtotal — treat backend total as merchandise-only and add gift.
  if (paidSubtotalGross <= 0.01) {
    return merchandiseOrderTotal + giftWrapNet;
  }

  return merchandiseOrderTotal;
}

type VoucherDiscountOptions = {
  undiscountedSubtotalGross?: number | null;
  paidSubtotalGross?: number | null;
};

/** Retail value of BXGY free-gift lines — synthetic carousel lines often lack line-level pricing. */
function resolveBxgyFreeGiftRetailValue(
  checkoutLines?: CheckoutLines[] | null,
  options?: VoucherDiscountOptions,
): number {
  const fromGiftLines = (checkoutLines ?? [])
    .filter((line) => !isPaidCheckoutMerchandiseLine(line))
    .filter(
      (line) =>
        isCheckoutGiftLine(line) ||
        isZeroPriceBxgyGiftLine(line) ||
        shouldShowLineAsFreeGift(line),
    )
    .reduce((sum, line) => {
      const quantity = line.quantity || 1;
      const fromVariant =
        resolveTaxedGrossAmount(line.variant?.pricing?.priceUndiscounted) ||
        resolveTaxedGrossAmount(line.variant?.pricing?.price);
      const retail = fromVariant > 0.01 ? fromVariant : getLineRetailUnitPrice(line);
      return sum + retail * quantity;
    }, 0);

  if (fromGiftLines > 0.01) {
    return fromGiftLines;
  }

  // No gift lines → do not infer a gift from undiscountedGross − line retail.
  // Line undiscountedUnitPrice.amount is net, so that gap is often VAT (e.g. 1239 − 1180 = 59),
  // which incorrectly turns a 100 voucher into 41.
  const hasGiftLikeLine = (checkoutLines ?? []).some(
    (line) =>
      isCheckoutGiftLine(line) ||
      isZeroPriceBxgyGiftLine(line) ||
      shouldShowLineAsFreeGift(line) ||
      !isPaidCheckoutMerchandiseLine(line),
  );
  if (!hasGiftLikeLine) {
    return 0;
  }

  const undiscounted = options?.undiscountedSubtotalGross ?? 0;
  const paidSubtotal = options?.paidSubtotalGross ?? 0;
  if (undiscounted > paidSubtotal + 0.01) {
    const paidRetail = sumMerchandiseRetailSubtotal(checkoutLines, null, paidSubtotal);
    return Math.max(0, undiscounted - paidRetail);
  }

  return 0;
}

type SubtotalMoney = {
  gross?: { amount?: number | null } | null;
  net?: { amount?: number | null } | null;
  tax?: { amount?: number | null } | null;
} | null | undefined;

/**
 * Coupon discount for order summary.
 * Always prefer checkout.discount.amount (voucher face value).
 * Do not derive from listGross − lineGross on tax-exclusive AE: that is VAT
 * residue (e.g. 840 − 782 = 58) while the coupon is still 100.
 */
export function resolveVoucherDiscountAmount(
  voucherCode?: string | null,
  subtotalDiscountAmount?: SubtotalMoney,
  checkoutDiscount?: { amount?: number | null } | null,
  checkoutLines?: CheckoutLines[] | null,
  options?: VoucherDiscountOptions,
): number {
  const fromCheckoutDiscount = checkoutDiscount?.amount ?? 0;
  const fromSubtotalDiscountGross = subtotalDiscountAmount?.gross?.amount ?? 0;
  const paidSubtotalGross = options?.paidSubtotalGross;

  if (!voucherCode) {
    return 0;
  }

  if (fromCheckoutDiscount > 0.01) {
    return fromCheckoutDiscount;
  }

  if (hasBxgyGiftLinesForSubtotalSummary(checkoutLines)) {
    const paidRetailSubtotal = sumMerchandiseRetailSubtotal(
      checkoutLines,
      null,
      paidSubtotalGross,
    );
    if (
      paidRetailSubtotal > 0.01 &&
      paidSubtotalGross != null &&
      paidSubtotalGross > 0.01 &&
      paidRetailSubtotal > paidSubtotalGross + 0.01
    ) {
      return paidRetailSubtotal - paidSubtotalGross;
    }

    const paidLines = (checkoutLines ?? []).filter(
      (line) => isPaidCheckoutMerchandiseLine(line) && !isCheckoutGiftLine(line),
    );
    const couponFromPaidLines = paidLines.reduce((sum, line) => {
      const quantity = line.quantity || 1;
      const retail = getLineRetailUnitPrice(line, {
        paidSubtotalGross,
        checkoutMerchandiseLineCount: paidLines.length,
      });
      const paid = line.unitPrice?.gross?.amount ?? 0;
      return sum + Math.max(0, retail - paid) * quantity;
    }, 0);
    if (couponFromPaidLines > 0.01) {
      return couponFromPaidLines;
    }

    const bxgyGiftValue = resolveBxgyFreeGiftRetailValue(checkoutLines, options);

    const voucherOnly = Math.max(0, fromCheckoutDiscount - bxgyGiftValue);
    if (voucherOnly > 0.01) {
      return voucherOnly;
    }

    if (fromSubtotalDiscountGross > 0) {
      if (bxgyGiftValue > 0.01) {
        if (fromSubtotalDiscountGross > bxgyGiftValue + 0.01) {
          return fromSubtotalDiscountGross - bxgyGiftValue;
        }
        if (Math.abs(fromSubtotalDiscountGross - bxgyGiftValue) <= 0.02) {
          return 0;
        }
      }
      return fromSubtotalDiscountGross;
    }

    return 0;
  }

  const inferredBxgyGift = resolveBxgyFreeGiftRetailValue(checkoutLines, options);
  if (inferredBxgyGift > 0.01 && fromSubtotalDiscountGross > inferredBxgyGift + 0.01) {
    return fromSubtotalDiscountGross - inferredBxgyGift;
  }

  if (fromSubtotalDiscountGross > 0) {
    return fromSubtotalDiscountGross;
  }

  return fromCheckoutDiscount;
}

/** Voucher discount on placed orders — coupon-only amount for BXGY + voucher carts. */
export function resolveOrderVoucherCheckoutDiscount(order?: {
  lines?: CheckoutLines[] | null;
  discounts?: Array<{ type?: string | null; amount?: { amount?: number | null } | null }> | null;
  subtotal?: {
    gross?: { amount?: number | null } | null;
    net?: { amount?: number | null } | null;
    tax?: { amount?: number | null } | null;
  } | null;
  undiscountedSubtotal?: { gross?: { amount?: number | null } | null } | null;
  subtotalDiscount?: { gross?: { amount?: number | null } | null } | null;
  total?: { gross?: { amount?: number | null } | null } | null;
  voucher?: { code?: string | null } | null;
} | null): { amount: number } | null {
  const fromSubtotalDiscount = order?.subtotalDiscount?.gross?.amount ?? 0;

  if (!order?.voucher?.code) {
    return null;
  }

  const hasBxgy = hasBxgyGiftLinesForSubtotalSummary(order?.lines);
  const subtotalGross = order?.subtotal?.gross?.amount ?? 0;

  const fromDiscounts = (order?.discounts ?? []).find(
    (entry) => `${entry.type ?? ""}`.toUpperCase() === "VOUCHER",
  )?.amount?.amount;

  if (fromDiscounts != null && fromDiscounts > 0) {
    return { amount: fromDiscounts };
  }

  if (hasBxgy && order?.voucher?.code) {
    const paidRetail = sumMerchandiseRetailSubtotal(order?.lines);
    const catalogueMarkdown = sumPaidMerchandiseCatalogueDiscount(order?.lines);
    const subtotalNet = order?.subtotal?.net?.amount ?? 0;
    const subtotalTax = order?.subtotal?.tax?.amount ?? 0;
    // AE: tax is 5% of catalog list (790 × 0.05 = 39.50), paid catalog sits on subtotal.net.
    const catalogPaid =
      paidRetail > 0.01 && subtotalNet > 0.01 && Math.abs(subtotalTax - paidRetail * 0.05) <= 0.05
        ? subtotalNet
        : subtotalGross;
    const couponOnly = Math.max(0, paidRetail - catalogPaid - catalogueMarkdown);
    if (couponOnly > 0.01) {
      return { amount: couponOnly };
    }
  }

  const paidLines = (order?.lines ?? []).filter(
    (line) => !isCheckoutGiftLine(line) && !isZeroPriceBxgyGiftLine(line),
  );
  const preCouponTotal = paidLines.reduce((sum, line) => {
    const quantity = line.quantity || 1;
    const undiscounted =
      line.undiscountedUnitPrice?.gross?.amount ??
      line.variant?.pricing?.priceUndiscounted?.gross?.amount ??
      line.unitPrice?.gross?.amount ??
      0;
    return sum + undiscounted * quantity;
  }, 0);
  const postCouponTotal = paidLines.reduce((sum, line) => {
    const quantity = line.quantity || 1;
    const paid = line.unitPrice?.gross?.amount ?? line.totalPrice?.gross?.amount ?? 0;
    return sum + paid * quantity;
  }, 0);
  const linePromoDiscount = Math.max(0, preCouponTotal - postCouponTotal);

  if (fromSubtotalDiscount > 0) {
    if (!hasBxgy) {
      return { amount: fromSubtotalDiscount };
    }
    if (linePromoDiscount <= 0.01 || fromSubtotalDiscount <= linePromoDiscount + 0.01) {
      return { amount: fromSubtotalDiscount };
    }
    return { amount: fromSubtotalDiscount - linePromoDiscount };
  }

  if (hasBxgy) {
    if (postCouponTotal > subtotalGross + 0.01) {
      return { amount: postCouponTotal - subtotalGross };
    }
    if (preCouponTotal > subtotalGross + 0.01 && linePromoDiscount <= 0.01) {
      return { amount: preCouponTotal - subtotalGross };
    }
  } else if (preCouponTotal > postCouponTotal + 0.01) {
    return { amount: preCouponTotal - postCouponTotal };
  }

  const undiscountedGross = order?.undiscountedSubtotal?.gross?.amount ?? 0;
  if (!hasBxgy && undiscountedGross > subtotalGross + 0.01) {
    return { amount: undiscountedGross - subtotalGross };
  }

  const totalGross = order?.total?.gross?.amount ?? 0;
  if (subtotalGross > totalGross + 0.01) {
    return { amount: subtotalGross - totalGross };
  }

  return null;
}

export function getAllEligibleGiftVariantIds(context: GiftCheckoutContext): string[] {
  const ids = new Set<string>();

  context.availableGiftPromotions?.forEach((promo) => {
    promo.eligibleGiftVariants?.forEach((variant) => {
      if (variant?.id) {
        ids.add(variant.id);
      }
    });
  });

  context.availableGifts?.forEach((gift) => {
    if (gift?.id) {
      ids.add(gift.id);
    }
  });

  return Array.from(ids);
}

/** Mirrors cart `activeGiftVariantIds` — null means single-gift mode (show all gift lines). */
export function getActiveGiftVariantIds(context: GiftCheckoutContext): string[] | null {
  const ids = new Set<string>();
  let totalGifts = 0;
  let hasOptOut = false;
  let requiresExplicitSelection = false;

  if (context.availableGiftPromotions?.length) {
    // Prefer merged offers so multi-gift selections across split API entries all count.
    const mergedPromos = getAvailableOfferPromotions(context.availableGiftPromotions);

    mergedPromos.forEach((promo) => {
      totalGifts += promo.eligibleGiftVariants?.length || 0;
      if (promo.optedOut) {
        hasOptOut = true;
      }
      if (promo.requiresSelection || getGiftQuantityForPromo(promo) > 1) {
        requiresExplicitSelection = true;
      }
      getSelectedGiftVariantIds(promo).forEach((id) => ids.add(id));
    });

    // Also collect from raw entries in case merge missed a selection.
    context.availableGiftPromotions.forEach((promo) => {
      getSelectedGiftVariantIds(promo).forEach((id) => ids.add(id));
    });

    const rawPromos = context.availableGiftPromotions;
    rawPromos.forEach((promo) => {
      const autoId = promo.eligibleGiftVariants?.[0]?.id;
      // Keep the free gift that Saleor auto-added, even if a same-named "pick"
      // offer briefly cleared this rule's selection while the user was choosing.
      if (autoId && !promo.optedOut && isAutoAddedSingleGiftPromo(promo, rawPromos)) {
        ids.add(autoId);
      }
    });

    // Keep free-gift lines already on checkout nested under the parent product
    // (Magrabi parity) — multi-select Available Offers can still change the pick.
    (context.lines ?? []).forEach((line) => {
      if ((isCheckoutGiftLine(line) || line.buyGetGiftFlag) && line.variant?.id) {
        ids.add(line.variant.id);
      }
    });
  } else if (context.availableGifts?.length) {
    totalGifts = context.availableGifts.length;
    if (context.selectedGiftVariant) {
      ids.add(context.selectedGiftVariant);
    }
  }

  // Single auto gift with no opt-out / multi-select — keep legacy "show all" behavior.
  if (totalGifts <= 1 && !hasOptOut && !requiresExplicitSelection) {
    return null;
  }

  return Array.from(ids);
}

export function isLineGiftForDisplay(line: CheckoutLines, allEligibleGiftVariantIds: string[]): boolean {
  // Paid trigger lines may share a variant id with carousel gifts — never treat them as gift rows.
  if (isPaidCheckoutMerchandiseLine(line)) {
    return false;
  }

  return (
    isCheckoutGiftLine(line) ||
    (allEligibleGiftVariantIds.length > 0 && allEligibleGiftVariantIds.includes(line?.variant?.id))
  );
}

function resolveSelectedGiftVariantId(context: GiftCheckoutContext): string {
  if (context.selectedGiftVariant) {
    return context.selectedGiftVariant;
  }

  const fromPromo = context.availableGiftPromotions?.find((promo) => promo.selectedGiftVariant?.id)
    ?.selectedGiftVariant?.id;
  if (fromPromo) {
    return fromPromo;
  }

  const totalEligible = getTotalEligibleGiftVariants(context);
  if (totalEligible === 1) {
    const singleFromPromo = context.availableGiftPromotions?.flatMap((promo) => promo.eligibleGiftVariants ?? [])[0];
    if (singleFromPromo?.id) {
      return singleFromPromo.id;
    }

    if (context.availableGifts?.length === 1) {
      return context.availableGifts[0].id;
    }
  }

  return "";
}

function getTotalEligibleGiftVariants(context: GiftCheckoutContext): number {
  const fromPromos =
    context.availableGiftPromotions?.reduce(
      (acc, promo) => acc + (promo.eligibleGiftVariants?.length || 0),
      0,
    ) ?? 0;

  if (fromPromos > 0) {
    return fromPromos;
  }

  return context.availableGifts?.length ?? 0;
}

function collectVisibleVariantIds(lines: CheckoutLines[]): Set<string> {
  const ids = new Set<string>();
  lines.forEach((line) => {
    if (line.variant?.id) {
      ids.add(line.variant.id);
    }
    (line.addOns ?? []).forEach((addOn) => {
      if (addOn.variant?.id) {
        ids.add(addOn.variant.id);
      }
    });
  });
  return ids;
}

function appendMissingSelectedGifts(
  visible: CheckoutLines[],
  context: GiftCheckoutContext,
  activeGiftVariantIds: string[] | null,
): CheckoutLines[] {
  if (isCheckoutPromotionLimitReached(context.lines)) {
    return enrichVisibleGiftLines(visible, context);
  }

  const existingVariantIds = collectVisibleVariantIds(visible);
  const giftsToAdd: Array<{ line: CheckoutLines; parentLineIds: string[]; parentVariantIds: string[] }> = [];
  const currency = context.currency ?? "";

  const shouldInclude = (variantId: string) =>
    activeGiftVariantIds === null || activeGiftVariantIds.includes(variantId);

  const mergedPromos = getAvailableOfferPromotions(context.availableGiftPromotions);
  const rawPromos = context.availableGiftPromotions ?? [];
  const parentByGiftVariantId = buildGiftVariantParentLineMap(rawPromos, context.lines);
  const autoSingleVariantIds = new Set(
    rawPromos
      .filter((promo) => !promo.optedOut && isAutoAddedSingleGiftPromo(promo, rawPromos))
      .map((promo) => promo.eligibleGiftVariants?.[0]?.id)
      .filter(Boolean) as string[],
  );

  const resolveParentsForGift = (promo: GiftPromotion, variantId: string) => {
    const mappedParentId = parentByGiftVariantId.get(variantId);
    if (mappedParentId) {
      const parentLine = (context.lines ?? []).find((line) => line.id === mappedParentId);
      return {
        parentLineIds: [mappedParentId],
        parentVariantIds: parentLine?.variant?.id ? [parentLine.variant.id] : [],
      };
    }
    return resolvePromoParentTargets(promo, context.lines);
  };

  context.availableGiftPromotions?.forEach((promo) => {
    if (promo.optedOut) {
      return;
    }

    const selectedVariants: AvailableGifts[] = [];
    if (promo.selectedGiftVariants?.length) {
      selectedVariants.push(...promo.selectedGiftVariants);
    } else if (promo.selectedGiftVariant) {
      selectedVariants.push(promo.selectedGiftVariant);
    } else if (
      promo.eligibleGiftVariants?.length === 1 &&
      autoSingleVariantIds.has(promo.eligibleGiftVariants[0].id)
    ) {
      selectedVariants.push(promo.eligibleGiftVariants[0]);
    }

    selectedVariants.forEach((giftVariant) => {
      if (
        giftVariant &&
        !existingVariantIds.has(giftVariant.id) &&
        shouldInclude(giftVariant.id) &&
        context.allowSyntheticGifts !== false
      ) {
        const { parentLineIds, parentVariantIds } = resolveParentsForGift(promo, giftVariant.id);
        giftsToAdd.push({
          line: checkoutLineFromAvailableGift(giftVariant, currency),
          parentLineIds,
          parentVariantIds,
        });
        existingVariantIds.add(giftVariant.id);
      }
    });
  });

  // After merge, one card may already list every selected gift even when the API
  // originally returned one promotion entry per gift variant.
  mergedPromos.forEach((promo) => {
    if (promo.optedOut) {
      return;
    }

    const selectedIds = getSelectedGiftVariantIds(promo);
    if (!selectedIds.length) {
      return;
    }

    const catalog = [
      ...(promo.eligibleGiftVariants ?? []),
      ...(promo.selectedGiftVariants ?? []),
      ...(promo.selectedGiftVariant ? [promo.selectedGiftVariant] : []),
    ];

    selectedIds.forEach((variantId) => {
      if (
        existingVariantIds.has(variantId) ||
        !shouldInclude(variantId) ||
        context.allowSyntheticGifts === false
      ) {
        return;
      }

      const giftVariant = catalog.find((variant) => variant.id === variantId);
      if (!giftVariant) {
        return;
      }

      const ownerRuleId = promo.variantRuleMap?.[variantId] ?? promo.promotionRuleId;
      const rawOwner =
        rawPromos.find((entry) => entry.promotionRuleId === ownerRuleId) ?? promo;
      const { parentLineIds, parentVariantIds } = resolveParentsForGift(rawOwner, variantId);

      giftsToAdd.push({
        line: checkoutLineFromAvailableGift(giftVariant, currency),
        parentLineIds,
        parentVariantIds,
      });
      existingVariantIds.add(variantId);
    });
  });

  const resolvedGiftSelection = resolveSelectedGiftVariantId(context);
  if (
    resolvedGiftSelection &&
    !existingVariantIds.has(resolvedGiftSelection) &&
    shouldInclude(resolvedGiftSelection)
  ) {
    const fromPromo = context.availableGiftPromotions
      ?.flatMap((promo) => promo.eligibleGiftVariants ?? [])
      .find((gift) => gift.id === resolvedGiftSelection);

    const gift = fromPromo ?? context.availableGifts?.find((item) => item.id === resolvedGiftSelection);

    if (gift && context.allowSyntheticGifts !== false) {
      const paidParents = visible.filter((line) => !isCheckoutGiftLine(line) && !line.buyGetGiftFlag);
      giftsToAdd.push({
        line: checkoutLineFromAvailableGift(gift, currency),
        parentLineIds: paidParents.length === 1 ? [paidParents[0].id] : [],
        parentVariantIds:
          paidParents.length === 1 && paidParents[0].variant?.id ? [paidParents[0].variant.id] : [],
      });
    }
  }

  // Place each selected gift as its own full cart card; all gifts after paid block.
  let withAddedGifts = visible;
  if (giftsToAdd.length > 0) {
    const remaining = [...giftsToAdd];
    const orderedPaid: CheckoutLines[] = [];
    const orderedGifts: CheckoutLines[] = [];

    visible.forEach((line) => {
      if (isCheckoutGiftLine(line) || line.buyGetGiftFlag) {
        orderedGifts.push(line);
        return;
      }
      orderedPaid.push(line);
      for (let index = 0; index < remaining.length; ) {
        const entry = remaining[index];
        const matchesParent =
          entry.parentLineIds.includes(line.id) ||
          (Boolean(line.variant?.id) && entry.parentVariantIds.includes(line.variant?.id as string));
        if (matchesParent) {
          orderedGifts.push({ ...entry.line, parent: { id: line.id } });
          remaining.splice(index, 1);
        } else {
          index += 1;
        }
      }
    });

    if (remaining.length > 0) {
      const paidParents = orderedPaid.filter((line) => !line.parent?.id);
      const soleParentId = paidParents.length === 1 ? paidParents[0].id : undefined;
      remaining.splice(0).forEach((entry) => {
        orderedGifts.push({
          ...entry.line,
          parent: soleParentId ? { id: soleParentId } : entry.line.parent,
        });
      });
    }

    // Paid block first, then all free gifts.
    withAddedGifts = [...orderedPaid, ...orderedGifts];
  }

  return enrichVisibleGiftLines(withAddedGifts, context);
}

/**
 * Resolves checkout/cart lines the same way the cart list does — keeps only selected
 * free gifts (BXGY promotions + Available Offers) and synthesizes display lines when needed.
 * Nested gift children under parent lines are preserved (MLC-1803 / MLC-1830).
 */
export function getVisibleGiftCheckoutLines(
  context: GiftCheckoutContext,
  options?: { forOrder?: boolean },
): CheckoutLines[] {
  const lines = context.lines ?? [];
  const merged = mergeCheckoutLines(lines);
  const allEligibleGiftVariantIds = getAllEligibleGiftVariantIds(context);

  if (options?.forOrder) {
    return merged;
  }

  if (isCheckoutPromotionLimitReached(merged)) {
    const eligibleIds = new Set(allEligibleGiftVariantIds);
    return merged
      .map((line) => {
        // Hide free gifts / gift-pool variants only — never hide charged merchandise (BXGY buy-X parent).
        // Saleor attaches giftType FREE_GIFT on the paid trigger line when PER_USER_LIMIT_REACHED.
        if (isPaidCheckoutMerchandiseLine(line)) {
          return {
            ...line,
            addOns: (line.addOns ?? []).filter(
              (addOn) => !isCheckoutGiftLine(addOn) && !addOn.buyGetGiftFlag && !eligibleIds.has(addOn.variant?.id),
            ),
          };
        }
        // Belt-and-suspenders: explicit non-gift + any money/catalogue still visible.
        const chargedAmount =
          (line.totalPrice?.gross?.amount ?? 0) > 0.01 || (line.unitPrice?.gross?.amount ?? 0) > 0.01;
        if (
          line.isGift === false &&
          line.isBuyxGetyGift === false &&
          !line.buyGetGiftFlag &&
          chargedAmount
        ) {
          return {
            ...line,
            addOns: (line.addOns ?? []).filter(
              (addOn) => !isCheckoutGiftLine(addOn) && !addOn.buyGetGiftFlag && !eligibleIds.has(addOn.variant?.id),
            ),
          };
        }
        if (isLineGiftForDisplay(line, allEligibleGiftVariantIds) || eligibleIds.has(line.variant?.id)) {
          return null;
        }
        return {
          ...line,
          addOns: (line.addOns ?? []).filter(
            (addOn) => !isCheckoutGiftLine(addOn) && !addOn.buyGetGiftFlag && !eligibleIds.has(addOn.variant?.id),
          ),
        };
      })
      .filter(Boolean) as CheckoutLines[];
  }

  const activeGiftVariantIds = getActiveGiftVariantIds(context);

  const visible = merged
    .map((line) => {
      const isRootGift =
        isLineGiftForDisplay(line, allEligibleGiftVariantIds) || Boolean(line.buyGetGiftFlag);

      if (isRootGift) {
        // Keep as a full Free gift card under the parent product (screenshot design).
        if (activeGiftVariantIds === null) {
          return line;
        }
        return activeGiftVariantIds.includes(line.variant?.id) ? line : null;
      }

      // Paid / parent line — keep lens add-ons only (gifts are sibling cards, not addOns).
      if (!line.addOns?.length) {
        return line;
      }

      const nextAddOns = line.addOns.filter((addOn) => {
        const isGiftChild = isCheckoutGiftLine(addOn) || Boolean(addOn.buyGetGiftFlag);
        if (!isGiftChild) {
          return true;
        }
        // Drop nested gift addOns — gifts render as separate full cards.
        return false;
      });

      return { ...line, addOns: nextAddOns };
    })
    .filter(Boolean) as CheckoutLines[];

  const visibleLines = appendMissingSelectedGifts(visible, context, activeGiftVariantIds);
  const nestedLines = nestGiftsUnderQualifyingParents(
    visibleLines,
    context.availableGiftPromotions,
  );
  const cardLines = promoteGiftAddOnsToSiblingLines(nestedLines);

  if (isCheckoutPromotionLimitReached(merged)) {
    return cardLines.filter((line) => !isSyntheticGiftLine(line));
  }

  return cardLines;
}

function getOrderLineMetaValue(line: CheckoutLines, key: string): string | null {
  return (hasArrayElements(line?.metadata) && line.metadata?.find((meta) => meta?.key === key)?.value) || null;
}

export function mergeOrderConfirmationLines(lines: CheckoutLines[]): CheckoutLines[] {
  const giftMerged = mergeCheckoutLines(lines || []);
  const addonGroups: Record<string, CheckoutLines[]> = {};
  const mergedLinesIn: CheckoutLines[] = [];

  if (!hasArrayElements(giftMerged)) {
    return [];
  }

  giftMerged.forEach((line: CheckoutLines) => {
    if (isCheckoutGiftLine(line)) {
      mergedLinesIn.push(line);
      return;
    }

    const parentCheckoutId = getOrderLineMetaValue(line, MetaDataKeys.PARENT_CHECKOUT_ID);
    if (parentCheckoutId) {
      if (!addonGroups[parentCheckoutId]) {
        addonGroups[parentCheckoutId] = [];
      }
      addonGroups[parentCheckoutId]?.push(line);
      return;
    }

    mergedLinesIn.push(line);
  });

  if (Object.keys(addonGroups).length > 0) {
    mergedLinesIn.forEach((line: CheckoutLines) => {
      const checkoutId = getOrderLineMetaValue(line, MetaDataKeys.CHECKOUT_ID);
      if (checkoutId && Object.keys(addonGroups).includes(checkoutId)) {
        line.addOns = addonGroups[checkoutId];
      }
    });
  }

  return mergedLinesIn;
}

export function getVisibleOrderConfirmationLines(
  order: {
    checkoutId?: string | null;
    lines?: CheckoutLines[] | null;
    total?: Checkout["totalPrice"];
    subtotal?: Checkout["subtotalPrice"];
  },
  options?: {
    enrichedGiftCatalog?: Record<string, AvailableGifts> | null;
    persistedSelection?: PersistedGiftCheckoutSelection | null;
  },
): { lines: CheckoutLines[]; giftContext: GiftCheckoutContext } {
  const addonMerged = mergeOrderConfirmationLines(order.lines ?? []);
  const giftContext = buildCheckoutGiftContext(
    {
      id: order.checkoutId ?? "",
      lines: addonMerged,
      availableGiftPromotions: undefined,
      availableGifts: undefined,
      selectedGiftVariant: undefined,
      totalPrice: order.total as unknown as Checkout["totalPrice"],
      subtotalPrice: order.subtotal as unknown as Checkout["subtotalPrice"],
    } as Pick<
      Checkout,
      | "id"
      | "lines"
      | "availableGiftPromotions"
      | "availableGifts"
      | "selectedGiftVariant"
      | "totalPrice"
      | "subtotalPrice"
    >,
    options,
  );

  return {
    lines: filterOrderGiftLines(getVisibleGiftCheckoutLines(giftContext)),
    giftContext,
  };
}

function getBxgyFreeGiftPromotionKey(line: CheckoutLines): string | null {
  const promo = line.appliedPromotions?.find(
    (p) => p.promotion?.type === promotionType.BUYXGETY && p.giftType === giftTypes.FREE_GIFT,
  );

  return promo?.id ?? promo?.name ?? null;
}

function pickSelectedBxgyGiftLine(candidates: CheckoutLines[]): CheckoutLines {
  return (
    candidates.find((line) => line.isBuyxGetyGift) ??
    candidates.find((line) => (line.unitPrice?.gross?.amount ?? 0) <= 0) ??
    candidates[0]
  );
}

/**
 * Keep all free (selected) gift lines per promotion. Only collapse when the
 * payload dumps non-free "eligible" candidates with no free flags.
 */
export function filterOrderGiftLines(lines: CheckoutLines[]): CheckoutLines[] {
  const giftLines = lines.filter(isCheckoutGiftLine);
  const regularLines = lines.filter((line) => !isCheckoutGiftLine(line));

  if (giftLines.length <= 1) {
    return lines;
  }

  const groups = new Map<string, CheckoutLines[]>();
  giftLines.forEach((line) => {
    const key = getBxgyFreeGiftPromotionKey(line) ?? "__bxgy_gifts__";
    const group = groups.get(key) ?? [];
    group.push(line);
    groups.set(key, group);
  });

  const visibleGifts = Array.from(groups.values()).flatMap((candidates) => {
    const free = candidates.filter(
      (line) => line.isBuyxGetyGift || (line.unitPrice?.gross?.amount ?? 0) <= 0,
    );
    if (free.length > 0) {
      return free;
    }
    return [pickSelectedBxgyGiftLine(candidates)];
  });

  return [...regularLines, ...visibleGifts];
}

export function isDisplayFreeGiftLine(
  line: CheckoutLines,
  context: GiftCheckoutContext,
): boolean {
  if (isPaidCheckoutMerchandiseLine(line)) {
    return false;
  }

  if (isCheckoutPromotionLimitReached(context.lines)) {
    return false;
  }

  if (!shouldShowLineAsFreeGift(line)) {
    return false;
  }

  const selectedId = resolveSelectedGiftVariantId(context);
  const allEligibleGiftVariantIds = getAllEligibleGiftVariantIds(context);

  if (
    selectedId &&
    line.variant?.id === selectedId &&
    (allEligibleGiftVariantIds.includes(selectedId) || isCheckoutGiftLine(line))
  ) {
    return true;
  }

  const activeGiftVariantIds = getActiveGiftVariantIds(context);

  if (!isLineGiftForDisplay(line, allEligibleGiftVariantIds)) {
    return false;
  }

  if (activeGiftVariantIds === null) {
    return true;
  }

  return activeGiftVariantIds.includes(line.variant?.id);
}

export type CartSubtotalSummary = {
  displayAmount: number;
  showDiscountRow: boolean;
  displayDiscountAmount: number;
  displayOrderTotal: number;
};

/**
 * Discount row with a voucher: catalogue markdowns on paid lines plus the coupon.
 * Add them — do not use retail − Saleor paid gross. AE VAT-inflated paid (488.74 + 140.76)
 * yields 160.50 instead of 200 (100 catalogue + 100 coupon).
 * Callers must pass catalogue-only markdowns — never BXGY free-gift retail or VAT-gross gaps.
 */
function resolveVoucherCatalogDisplayDiscount(
  voucherFaceValue: number,
  catalogueMarkdown: number,
): number {
  return Math.max(0, voucherFaceValue) + Math.max(0, catalogueMarkdown);
}

/**
 * Cart-page voucher pricing (Magrabi-aligned).
 *
 * Subtotal → catalogue / line retail (never after-coupon paid when −coupon is shown)
 * Discount → checkout.discount (coupon) + catalogue markdowns on paid lines only
 * Order total → prefer totalPrice when it matches list − discount; else list − discount
 * Never subtotalPrice − coupon (1937 − 100 = 1837). Never both Subtotal=paid and −coupon.
 *
 * When a BXGY gift line is present: list from paid lines only (exclude gift retail).
 * Payment promo is stripped from Discount and added back into Order Total (cart ignores payment).
 */
export function getCartPageLineDerivedSummary(
  checkoutLines: CheckoutLines[] | undefined,
  subtotalPrice?: SubtotalMoney,
  giftWrapPriceAmount = 0,
  giftWrapDiscountAmount = 0,
  giftWrapEnabled = false,
  checkoutDiscount?: { amount?: number | null } | null,
  undiscountedSubtotalPrice?: SubtotalMoney,
  totalPrice?: SubtotalMoney,
  paymentDiscountAmount?: SubtotalMoney,
): CartSubtotalSummary {
  const hasBxgyGifts = hasBxgyFreeGiftProductLines(checkoutLines);
  const paidSubtotal = subtotalPrice?.gross?.amount ?? 0;
  const backendTotal = totalPrice?.gross?.amount ?? 0;
  const paymentDiscount = paymentDiscountAmount?.gross?.amount ?? 0;
  // Gift retail must not inflate Subtotal (e.g. 2037 + 380).
  const backendUndiscounted = hasBxgyGifts ? 0 : (undiscountedSubtotalPrice?.gross?.amount ?? 0);

  const linesPaid = (checkoutLines ?? []).reduce((sum, line) => {
    if (
      isCheckoutGiftLine(line) ||
      Boolean(line?.buyGetGiftFlag) ||
      isZeroPriceBxgyGiftLine(line)
    ) {
      return sum;
    }

    const lineTotal = getCheckoutPriceAmount(line?.totalPrice);
    if (lineTotal != null) {
      return sum + lineTotal;
    }
    const unit = getCheckoutPriceAmount(line?.unitPrice) ?? 0;
    return sum + unit * (line.quantity || 1);
  }, 0);

  const lineRetailSubtotal = sumMerchandiseRetailSubtotal(
    checkoutLines,
    hasBxgyGifts ? null : backendUndiscounted > 0.01 ? backendUndiscounted : null,
    paidSubtotal > 0.01 ? paidSubtotal : null,
  );

  const voucherFaceValue = checkoutDiscount?.amount ?? 0;
  const catalogueDiscount = sumPaidMerchandiseCatalogueDiscount(checkoutLines);

  let displayAmount = 0;
  if (hasBxgyGifts) {
    displayAmount =
      lineRetailSubtotal > 0.01 ? lineRetailSubtotal : paidSubtotal > 0.01 ? paidSubtotal : linesPaid;
  } else if (lineRetailSubtotal > 0.01 && backendUndiscounted > 0.01) {
    displayAmount = preferVatInclusiveAmount(backendUndiscounted, lineRetailSubtotal);
  } else if (lineRetailSubtotal > 0.01) {
    displayAmount = lineRetailSubtotal;
  } else if (backendUndiscounted > 0.01) {
    displayAmount = backendUndiscounted;
  } else if (voucherFaceValue > 0.01 && paidSubtotal > 0.01) {
    // Restore list when only after-coupon subtotal is available (never show paid as Subtotal with −coupon).
    displayAmount = paidSubtotal + voucherFaceValue;
  } else {
    displayAmount = paidSubtotal > 0.01 ? paidSubtotal : linesPaid;
  }

  // Guard: never Subtotal ≈ after-coupon paid while still showing −coupon (double-count).
  if (voucherFaceValue > 0.01 && paidSubtotal > 0.01 && Math.abs(displayAmount - paidSubtotal) <= 1.01) {
    displayAmount = paidSubtotal + voucherFaceValue;
  }

  const rawDiscount =
    voucherFaceValue > 0.01
      ? resolveVoucherCatalogDisplayDiscount(voucherFaceValue, catalogueDiscount)
      : Math.max(0, displayAmount - (linesPaid > 0.01 ? linesPaid : paidSubtotal));
  // Do not merge payment promo into cart Discount row.
  const displayDiscountAmount = Math.max(0, rawDiscount - paymentDiscount);

  const giftWrapNet = giftWrapEnabled
    ? Math.max(0, giftWrapPriceAmount - giftWrapDiscountAmount)
    : 0;
  const expectedMerchandise = Math.max(0, displayAmount - displayDiscountAmount);

  // Prefer BE totalPrice when it matches list − discount; never use paidSubtotal − coupon.
  let patchedMerchandise = expectedMerchandise;
  if (backendTotal > 0.01 && Math.abs(backendTotal - expectedMerchandise) <= 1.01) {
    patchedMerchandise = backendTotal;
  } else {
    const paidHint =
      linesPaid > 0.01 ? linesPaid : backendTotal > 0.01 ? backendTotal : expectedMerchandise;
    patchedMerchandise = resolveCatalogVoucherPaidTotal(
      displayAmount,
      displayDiscountAmount,
      paidHint,
    );
  }

  // Payment already taken out of BE paid/total — add back so cart Order Total ignores payment promo.
  const displayOrderTotal = Math.max(0, patchedMerchandise + paymentDiscount + giftWrapNet);

  return {
    displayAmount,
    showDiscountRow: displayDiscountAmount > 0.01,
    displayDiscountAmount,
    displayOrderTotal,
  };
}

/**
 * Cart-page only — keep shipping/payment on the shared Saleor checkout; adjust display.
 *
 * When !voucherCode (payment/shipping order promo lives on checkout.discount):
 *   Subtotal     → undiscountedSubtotalPrice (paid-line retail when BXGY free gift is present)
 *   Cart discount → catalogue line promo + MLC-1307 VIP (checkout.discount vipExtra)
 *                   Payment/shipping merchandise order promo beyond VIP is stripped
 *   Order total   → subtotal shown − cart discount shown (+ net gift wrap). Derived from the
 *                   displayed rows, not subtotalPrice, which already has the payment promo cut.
 *
 * merchandiseOrderDiscount = max(0, discount.amount − shippingDiscountAmount − paymentDiscountAmount)
 * Prefer paymentDiscountAmount over inferring from availablePaymentGateways.applicablePromotions.
 *
 * When voucherCode is set, discount may be the voucher — do not use this helper; keep getCartSubtotalSummary.
 * Checkout summary must keep using Saleor fields via getCartSubtotalSummary.
 */
export function getCartPageIgnoreOrderDiscountSummary(
  checkoutLines: CheckoutLines[] | undefined,
  subtotalPrice?: SubtotalMoney,
  undiscountedSubtotalPrice?: SubtotalMoney,
  subtotalDiscountAmount?: SubtotalMoney,
  checkoutDiscount?: { amount?: number | null } | null,
  giftWrapPriceAmount = 0,
  giftWrapDiscountAmount = 0,
  giftWrapEnabled = false,
  shippingDiscountAmount?: SubtotalMoney,
  paymentDiscountAmount?: SubtotalMoney,
): CartSubtotalSummary {
  const shippingDiscount = shippingDiscountAmount?.gross?.amount ?? 0;
  const paymentDiscount = paymentDiscountAmount?.gross?.amount ?? 0;
  const rawOrderDiscount = checkoutDiscount?.amount ?? 0;
  // Shipping/payment promos may also sit on checkout.discount but were never taken from subtotalPrice.
  const merchandiseOrderDiscount = Math.max(
    0,
    rawOrderDiscount - shippingDiscount - paymentDiscount,
  );
  const paidSubtotal = subtotalPrice?.gross?.amount ?? 0;
  const giftWrapNet = giftWrapEnabled
    ? Math.max(0, giftWrapPriceAmount - giftWrapDiscountAmount)
    : 0;
  const subtotalDiscount = subtotalDiscountAmount?.gross?.amount ?? 0;

  // BXGY free gift: API undiscounted/subtotalDiscount include gift retail — use paid lines only.
  if (hasBxgyGiftLinesForSubtotalSummary(checkoutLines)) {
    const lineRetailSubtotal = sumMerchandiseRetailSubtotal(
      checkoutLines,
      null,
      paidSubtotal > 0 ? paidSubtotal : null,
    );
    const displayAmount = lineRetailSubtotal > 0.01 ? lineRetailSubtotal : paidSubtotal;
    const linePromoDiscount = sumPaidMerchandisePromoDiscount(checkoutLines);
    const linesPaid = Math.max(0, displayAmount - linePromoDiscount);
    const vipExtra = resolveMerchandiseCheckoutExtraDiscount(
      merchandiseOrderDiscount,
      linePromoDiscount,
      linesPaid,
      null,
    );
    const displayDiscountAmount = linePromoDiscount + vipExtra;
    // Total must match the rows above it: subtotalPrice can't be trusted here because the
    // payment promo is already deducted from it (and is not always reported back to us).
    const displayOrderTotal = Math.max(0, displayAmount - displayDiscountAmount) + giftWrapNet;

    return {
      displayAmount,
      showDiscountRow: displayDiscountAmount > 0.01,
      displayDiscountAmount,
      displayOrderTotal,
    };
  }

  const undiscounted = undiscountedSubtotalPrice?.gross?.amount ?? 0;
  const linePromoDiscount = sumPaidMerchandisePromoDiscount(checkoutLines);
  const displayAmount =
    undiscounted > 0.01
      ? undiscounted
      : paidSubtotal + Math.max(0, subtotalDiscount - merchandiseOrderDiscount - paymentDiscount);
  const linesPaid = Math.max(0, displayAmount - linePromoDiscount);
  const vipExtra = resolveMerchandiseCheckoutExtraDiscount(
    merchandiseOrderDiscount,
    linePromoDiscount,
    linesPaid,
    null,
  );
  // Strip residual non-VIP order promo only; keep MLC-1307 VIP early-access amount.
  const orderPromoToIgnore = Math.max(0, merchandiseOrderDiscount - vipExtra);
  const displayDiscountAmount = Math.max(
    0,
    subtotalDiscount - orderPromoToIgnore - paymentDiscount,
  );
  const resolvedDisplayAmount =
    undiscounted > 0.01 ? undiscounted : paidSubtotal + paymentDiscount + displayDiscountAmount;
  // Total must match the rows above it: subtotalPrice can't be trusted here because the
  // payment promo is already deducted from it (and is not always reported back to us).
  const displayOrderTotal =
    Math.max(0, resolvedDisplayAmount - displayDiscountAmount) + giftWrapNet;

  return {
    displayAmount: resolvedDisplayAmount,
    showDiscountRow: displayDiscountAmount > 0.01,
    displayDiscountAmount,
    displayOrderTotal,
  };
}

/** MLC-1307: VIP extra from checkout.discount. Skip for vouchers or when it matches line promo. */
function resolveMerchandiseCheckoutExtraDiscount(
  checkoutDiscountAmount: number,
  linePromoDiscount: number,
  linesPaid: number,
  voucherCode?: string | null,
): number {
  if (voucherCode || checkoutDiscountAmount <= 0.01) {
    return 0;
  }
  if (linePromoDiscount > 0.01 && Math.abs(checkoutDiscountAmount - linePromoDiscount) < 0.02) {
    return 0;
  }
  if (linesPaid > 0.01 && checkoutDiscountAmount > linesPaid + 0.01) {
    return 0;
  }
  return checkoutDiscountAmount;
}

/** Prefer backend totalPrice; ignore BXGY totals inflated by free-gift retail. */
function resolveMerchandiseOrderTotal(
  backendTotalPrice: number,
  linesPaid: number,
  vipExtra: number,
): number {
  const expectedMerchandise = Math.max(0, linesPaid - vipExtra);
  if (backendTotalPrice <= 0.01) {
    return expectedMerchandise;
  }
  if (vipExtra <= 0.01) {
    return backendTotalPrice;
  }
  const maxNonMerchandiseBuffer = 300;
  if (backendTotalPrice > expectedMerchandise + maxNonMerchandiseBuffer) {
    return expectedMerchandise;
  }
  return backendTotalPrice;
}

/** Subtotal/order discount for the generic "Discount" row — excludes gift-wrap promo. */
function resolveOrderSubtotalDiscountAmount(
  subtotalDiscountAmount?: SubtotalMoney,
  checkoutDiscount?: { amount?: number | null } | null,
  giftWrapDiscountAmount = 0,
  giftWrapEnabled = false,
): number {
  const fromSubtotalDiscount = subtotalDiscountAmount?.gross?.amount ?? 0;
  if (fromSubtotalDiscount > 0) {
    return fromSubtotalDiscount;
  }

  const fromCheckoutDiscount = checkoutDiscount?.amount ?? 0;
  if (fromCheckoutDiscount <= 0) {
    return 0;
  }

  if (giftWrapEnabled && giftWrapDiscountAmount > 0) {
    return Math.max(0, fromCheckoutDiscount - giftWrapDiscountAmount);
  }

  return fromCheckoutDiscount;
}

/**
 * Catalogue + order promo savings on paid merchandise only.
 * Explicitly skips BXGY / order free-gift lines so gift retail never enters the Discount row.
 */
function sumPaidMerchandisePromoDiscount(checkoutLines?: CheckoutLines[] | null): number {
  return (checkoutLines ?? []).reduce((sum, line) => {
    if (!isPaidCheckoutMerchandiseLine(line) || isCheckoutGiftLine(line) || isZeroPriceBxgyGiftLine(line)) {
      return sum;
    }

    const quantity = line.quantity || 1;
    const retail = getLineRetailUnitPrice(line) * quantity;
    const lineTotal = getCheckoutPriceAmount(line?.totalPrice);
    const unit = getCheckoutPriceAmount(line?.unitPrice);
    const paid = lineTotal != null ? lineTotal : (unit ?? 0) * quantity;

    return sum + Math.max(0, retail - paid);
  }, 0);
}

/** Catalogue markdowns on paid lines only (250 − 150 = 100). Ignores coupon and AE VAT-gross paid. */
function sumPaidMerchandiseCatalogueDiscount(checkoutLines?: CheckoutLines[] | null): number {
  return (checkoutLines ?? []).reduce((sum, line) => {
    if (!isPaidCheckoutMerchandiseLine(line) || isCheckoutGiftLine(line) || isZeroPriceBxgyGiftLine(line)) {
      return sum;
    }

    const quantity = line.quantity || 1;
    const retail = getLineRetailUnitPrice(line);
    const promotion = getEligibleCataloguePromotionForCard(line.appliedPromotions);
    if (!promotion || retail <= 0.01) {
      return sum;
    }

    const sale = getCataloguePromotionUnitPrice(
      retail,
      promotion,
      line.variant?.pricing?.discount?.gross?.amount,
    );
    return sum + Math.max(0, retail - sale) * quantity;
  }, 0);
}

/** Voucher / coupon discount — keeps legacy max logic. */
function resolveCheckoutDiscountAmount(
  undiscountedSubtotalPrice?: SubtotalMoney,
  subtotalPrice?: SubtotalMoney,
  subtotalDiscountAmount?: SubtotalMoney,
  totalPrice?: SubtotalMoney,
  checkoutDiscount?: { amount?: number | null } | null,
): number {
  const backendOrderTotal = totalPrice?.gross?.amount ?? subtotalPrice?.gross?.amount ?? 0;
  const undiscountedAmount =
    undiscountedSubtotalPrice?.gross?.amount ??
    subtotalPrice?.gross?.amount ??
    0;

  const fromSubtotalDiscount = subtotalDiscountAmount?.gross?.amount ?? 0;
  const fromCheckoutDiscount = checkoutDiscount?.amount ?? 0;
  const fromPriceDelta =
    undiscountedAmount > backendOrderTotal + 0.01
      ? undiscountedAmount - backendOrderTotal
      : 0;

  return Math.max(fromSubtotalDiscount, fromCheckoutDiscount, fromPriceDelta);
}

/** Cart/checkout/order summary — prefer Saleor money fields; do not invent totals via FE Math. */
export function getCartSubtotalSummary(
  checkoutLines: CheckoutLines[] | undefined,
  subtotalPrice?: SubtotalMoney,
  undiscountedSubtotalPrice?: SubtotalMoney,
  subtotalDiscountAmount?: SubtotalMoney,
  totalPrice?: SubtotalMoney,
  voucherCode?: string | null,
  checkoutDiscount?: { amount?: number | null } | null,
  giftWrapDiscountAmount = 0,
  giftWrapEnabled = false,
  giftWrapPriceAmount = 0,
): CartSubtotalSummary {
  const backendOrderTotal = totalPrice?.gross?.amount ?? subtotalPrice?.gross?.amount ?? 0;

  const discountAmount = voucherCode
    ? resolveCheckoutDiscountAmount(
        undiscountedSubtotalPrice,
        subtotalPrice,
        subtotalDiscountAmount,
        totalPrice,
        checkoutDiscount,
      )
    : resolveOrderSubtotalDiscountAmount(
        subtotalDiscountAmount,
        checkoutDiscount,
        giftWrapDiscountAmount,
        giftWrapEnabled,
      );

  const voucherDiscountAmount = resolveVoucherDiscountAmount(
    voucherCode,
    subtotalDiscountAmount,
    checkoutDiscount,
    checkoutLines,
    {
      undiscountedSubtotalGross: undiscountedSubtotalPrice?.gross?.amount,
      paidSubtotalGross: subtotalPrice?.gross?.amount,
    },
  );

  const hasBxgyGifts = hasBxgyFreeGiftProductLines(checkoutLines);
  // Never feed checkout undiscounted (includes free-gift retail) into line retail when BXGY gifts exist.
  const lineRetailSubtotal = sumMerchandiseRetailSubtotal(
    checkoutLines,
    hasBxgyGifts ? null : undiscountedSubtotalPrice?.gross?.amount,
    subtotalPrice?.gross?.amount,
  );

  const giftWrapNet =
    giftWrapEnabled && giftWrapPriceAmount > 0.01
      ? Math.max(0, giftWrapPriceAmount - (giftWrapDiscountAmount > 0 ? giftWrapDiscountAmount : 0))
      : 0;

  if (hasBxgyGifts) {
    const backendSubtotal = subtotalPrice?.gross?.amount ?? 0;
    const backendTotalPrice = totalPrice?.gross?.amount ?? 0;
    const postPromotionSubtotal = backendSubtotal || lineRetailSubtotal || 0;
    const displayAmount =
      voucherDiscountAmount > 0
        ? lineRetailSubtotal > 0
          ? lineRetailSubtotal
          : postPromotionSubtotal + voucherDiscountAmount
        : lineRetailSubtotal > 0
          ? lineRetailSubtotal
          : postPromotionSubtotal;
    const linePromoDiscount = sumPaidMerchandisePromoDiscount(checkoutLines);
    const catalogueDiscount = sumPaidMerchandiseCatalogueDiscount(checkoutLines);
    const linesPaid = Math.max(0, displayAmount - linePromoDiscount);
    const vipExtra = resolveMerchandiseCheckoutExtraDiscount(
      checkoutDiscount?.amount ?? 0,
      linePromoDiscount,
      linesPaid,
      voucherCode,
    );

    let displayDiscountAmount =
      voucherCode && voucherDiscountAmount > 0
        ? resolveVoucherCatalogDisplayDiscount(voucherDiscountAmount, catalogueDiscount)
        : linePromoDiscount + vipExtra;

    if (
      !voucherCode &&
      vipExtra <= 0.01 &&
      displayDiscountAmount > 0.01 &&
      Math.abs(backendOrderTotal - displayAmount) < 0.02
    ) {
      displayDiscountAmount = 0;
    }

    if (
      !voucherCode &&
      vipExtra <= 0.01 &&
      displayDiscountAmount <= 0.01 &&
      (subtotalDiscountAmount?.gross?.amount ?? 0) > 0.01 &&
      Math.abs(backendOrderTotal - displayAmount) >= 0.02
    ) {
      displayDiscountAmount = linePromoDiscount;
    }

    const merchandiseOrderTotal =
      voucherCode && voucherDiscountAmount > 0
        ? resolveDisplayOrderTotal(
            backendOrderTotal,
            displayAmount,
            displayDiscountAmount,
            voucherCode,
            backendSubtotal,
            linesPaid,
          )
        : resolveMerchandiseOrderTotal(
            backendTotalPrice > 0 ? backendTotalPrice : backendOrderTotal,
            linesPaid,
            vipExtra,
          );

    const displayOrderTotal = resolveOrderTotalWithGiftWrap(
      merchandiseOrderTotal,
      backendSubtotal,
      giftWrapNet,
    );
    return {
      displayAmount,
      showDiscountRow: displayDiscountAmount > 0.01,
      displayDiscountAmount,
      displayOrderTotal,
    };
  }

  const backendUndiscounted = undiscountedSubtotalPrice?.gross?.amount ?? 0;
  const backendSubtotal = subtotalPrice?.gross?.amount ?? 0;
  // Checkout undiscounted can be net (790); line retail is VAT-inclusive (829.50) like PDP.
  let displayAmount = backendUndiscounted || backendSubtotal || 0;
  if (lineRetailSubtotal > 0.01 && backendUndiscounted > 0.01) {
    displayAmount = preferVatInclusiveAmount(backendUndiscounted, lineRetailSubtotal);
  } else if (lineRetailSubtotal > 0) {
    displayAmount = lineRetailSubtotal;
  } else if (voucherCode) {
    displayAmount = backendSubtotal || 0;
  }

  const linePromoDiscount = sumPaidMerchandisePromoDiscount(checkoutLines);
  const catalogueDiscount = sumPaidMerchandiseCatalogueDiscount(checkoutLines);
  const linesPaid = Math.max(0, displayAmount - linePromoDiscount);
  const vipExtra = resolveMerchandiseCheckoutExtraDiscount(
    checkoutDiscount?.amount ?? 0,
    linePromoDiscount,
    linesPaid,
    voucherCode,
  );

  let resolvedDiscountAmount = voucherCode
    ? resolveVoucherCatalogDisplayDiscount(voucherDiscountAmount, catalogueDiscount)
    : Math.max(discountAmount, linePromoDiscount + vipExtra);

  if (!voucherCode && vipExtra <= 0.01 && resolvedDiscountAmount > 0.01) {
    const giftSizedGap =
      backendUndiscounted > backendOrderTotal + 0.01
        ? backendUndiscounted - backendOrderTotal
        : 0;
    // subtotalDiscount often = free-gift retail (+ small payment promo). When Order Total
    // already matches paid-line retail, that gap is not a real Discount Amount row.
    if (
      giftSizedGap > 0.01 &&
      resolvedDiscountAmount + 0.01 >= giftSizedGap &&
      lineRetailSubtotal > 0.01 &&
      Math.abs(backendOrderTotal - lineRetailSubtotal) < 0.51
    ) {
      resolvedDiscountAmount = 0;
    } else if (lineRetailSubtotal > 0 && Math.abs(lineRetailSubtotal - backendSubtotal) < 0.02) {
      resolvedDiscountAmount = 0;
    } else if (
      lineRetailSubtotal > 0.01 &&
      Math.abs(displayAmount - backendOrderTotal) < 0.51
    ) {
      resolvedDiscountAmount = 0;
    }
  }

  const merchandiseOrderTotal =
    voucherCode && resolvedDiscountAmount > 0.01
      ? resolveDisplayOrderTotal(
          totalPrice?.gross?.amount ?? backendOrderTotal,
          displayAmount,
          resolvedDiscountAmount,
          voucherCode,
          backendSubtotal,
          linesPaid,
        )
      : resolveMerchandiseOrderTotal(
          totalPrice?.gross?.amount ?? backendOrderTotal,
          linesPaid,
          vipExtra,
        );
  const displayOrderTotal = resolveOrderTotalWithGiftWrap(
    merchandiseOrderTotal,
    backendSubtotal,
    giftWrapNet,
  );

  return {
    displayAmount,
    showDiscountRow: resolvedDiscountAmount > 0.01,
    displayDiscountAmount: resolvedDiscountAmount,
    displayOrderTotal,
  };
}
